// strings.js - i18n scaffolding for V6 (V6_DESIGN §5.8, V6_PLAN Phase 0b #2).
//
// Rules:
//   - Every NEW V6 user-visible string goes through t('some.key').
//   - Inherited V5 strings are retrofitted lazily - only when the code around
//     them is being touched anyway. No blanket extraction sprint.
//   - English is the complete reference dictionary. Other languages (Dutch is
//     the expected second) override per key; any missing key falls back to
//     English, and an unknown key falls back to the key itself so a typo is
//     visible on screen instead of rendering blank.
//
// Usage:
//   import { t } from './strings.js';
//   t('fitness.timer.start')                      -> 'Start'
//   t('player.request.received', { name: 'Mia' }) -> placeholders use {name}
//
// The active language comes from CONFIG.i18n.lang ('en' until Dutch lands).

import { CONFIG } from './config.js';

const STRINGS = {

  en: {
    // ── Common building blocks (shared across V6 modules) ────────────────────
    'common.save':    'Save',
    'common.cancel':  'Cancel',
    'common.close':   'Close',
    'common.back':    'Back',
    'common.loading': 'Loading...',
    'common.error':   'Something went wrong. Please try again.',

    // ── Gate / login screen ──────────────────────────────────────────────────
    'gate.subtitle':        'Player development, season by season',
    'gate.codeLabel':       'Team access code',
    'gate.codePlaceholder': 'Enter your code',
    'gate.continue':        'Continue →',

    // ── FitnessTimer (generalised Bronco engine, V6_DESIGN §5.6) ─────────────
    'fitness.timer.start':  'Start',
    'fitness.timer.stop':   'Stop',
    'fitness.timer.reset':  'Reset',
    'fitness.state.idle':     'Not started',
    'fitness.state.running':  'Running',
    'fitness.state.finished': 'Finished',

    // ── Bronco plausibility guard (Phase 0c Fix 2) ───────────────────────────
    'fitness.guard.title':   'Very fast time - {time}',
    'fitness.guard.body':    'That is unusually quick for a full Bronco. Record it for {bib} anyway, or cancel if it was an accidental tap?',
    'fitness.guard.record':  'Record {time}',
    'fitness.guard.cancel':  'Cancel - accidental tap',
    'fitness.flag.implausible': 'unusually fast - double-check',

    // ── Bronco completeness (Phase 0c Fix 2, director view) ──────────────────
    'bronco.complete.allTimed':   'All {total} present players have a time.',
    'bronco.complete.missing':    '{missing} of {total} present players have NO time yet.',
    'bronco.complete.flagged':    '{count} time(s) look implausibly fast - open the results to check.',
    'bronco.complete.noTimes':    'No times recorded yet.',

    // ── Unmatched-bib resolve flow (Phase 0c Fix 1) ──────────────────────────
    'sel.resolve.warning':       '{count} bib(s) are not linked to a player. Their results will not count in player history. Resolve them before activating.',
    'sel.resolve.button':        'Resolve unmatched',
    'sel.resolve.title':         'Link bib to a player',
    'sel.resolve.searchHint':    'Type to search the player list...',
    'sel.resolve.noResults':     'No players match. Mark as external if this is a guest from outside the club.',
    'sel.resolve.external':      'Mark as external (guest)',
    'sel.resolve.skip':          'Skip for now',
    'sel.resolve.stop':          'Stop resolving',
    'sel.resolve.linked':        'Linked to {name}',
    'sel.resolve.markedExternal':'Marked as external',
    'sel.resolve.allDone':       'All bibs resolved',
    'sel.resolve.tagUnlinked':   'NOT LINKED',
    'sel.resolve.tagExternal':   'EXTERNAL',
    'sel.activate.unresolvedTitle': 'Unresolved bibs',
    'sel.activate.unresolvedBody':  '{count} bib(s) are not linked to a player and not marked external. Their marks and times will be invisible in player history. Resolve them first?',
    'sel.activate.resolveFirst':    'Resolve now',
    'sel.activate.anyway':          'Activate anyway',

    // ── Duplicate trainings (Phase 0c Fix 3b) ────────────────────────────────
    'sel.dup.banner':       'Possible duplicate trainings: {names}. Keep the copy that carries marks; archive the other.',
    'sel.dup.tag':          'duplicate?',
    'sel.dup.createTitle':  'Same name already exists',
    'sel.dup.createBody':   'A training called "{name}" already exists this season. Create another with the same name anyway?',
    'sel.dup.createAnyway': 'Create anyway',
    'sel.dup.bulkHint':     'same name as an existing training',

    // ── Coaches & staff manager (Phase 0c Fix 3) ─────────────────────────────
    'staff.title':          'Coaches & staff',
    'staff.subtitle':       'One record per real person',
    'staff.hint':           'Spelling variants of the same person should be merged: open the variant with the wrong name and merge it into the right one. Evaluations and history follow automatically.',
    'staff.empty':          'No staff records yet. They are created automatically from evaluation and selection data after the Phase 0c migration runs.',
    'staff.evalCount':      '{count} evaluation(s)',
    'staff.aliasCount':     '{count} spelling(s)',
    'staff.add':            '+ Add person',
    'staff.anytimeBadge':   'Evaluates anytime',
    'grid.loadFailed':      'Could not load the squad grid.',
    'grid.searchPh':        'Search a name, team or coach...',
    'grid.gridPlayer':      'Player',
    'grid.gridTeam':        'Team',
    'grid.gridBand':        'Band',
    'grid.gridBorn':        'Born',
    'grid.gridOverall':     'Overall',
    'grid.gridLastSeen':    'Last seen',
    'grid.gridEvals':       'Evals',
    'grid.gridCoach':       'Coach',
    'grid.never':           'never',
    // When the last-seen fetch fails, a date is not "never": it is not known.
    'grid.seenUnknown':     'unknown',
    'grid.filterUnseen':    'Not evaluated',
    'grid.filterLate':      'Born Sep to Dec',
    'grid.statChildren':    'Children',
    'grid.statChildrenNote':'in the club this season',
    'grid.statSeen':        'Evaluated',
    'grid.statUnseen':      'Never evaluated',
    'grid.statUnseenNote':  'no record at all',
    'grid.statStale':       'Over 90 days',
    'grid.statStaleNote':   'seen, but a while ago',
    'grid.statLate':        'Born Sep to Dec',
    'grid.statLateNote':    'youngest in their year',
    'grid.statUnavailable': 'could not be loaded',
    'grid.evalsFailed':     'Last-seen dates could not be loaded, so the dates and the over-90-days count are missing. Every other figure here is correct.',
    'grid.count':           'Showing {shown} of {total} children',
    'staff.plansBadge':     'Season plan',
    'staff.evaluatorBadge': 'Reviews staff',
    'staff.access':         'Access',
    'staff.accessTitle':    'Access for {name}',
    'staff.accessWhatThey': 'What they are',
    'staff.accessCanReach': 'What they can reach',
    'staff.accessSaved':    'Access updated for {name}',
    'staff.badgeTitle':     'DTA badge',
    'staff.badgeHint':      'Shown in this person\u2019s own Coach Hub header. Recognition only: it grants nothing and nobody else sees it. The badge names are the ones printed on the artwork, so they read the same in English and Dutch.',
    'staff.badgeNone':      'No badge',
    'staff.badgePreview':   'Wearing the {name} badge',
    'staff.accessTiming':   'Changes apply straight away, even to someone already using the app: the club checks these every time, so taking something away works immediately. The tabs and buttons they see refresh the next time they enter their code.',
    'staff.swPlans':        'The season plan',
    'staff.swPlansHint':    'This week’s practice on their home screen, the full plan for their age, and the drill link from a weak skill.',
    'staff.swAnytime':      'Evaluate any pillar, any time',
    'staff.swAnytimeHint':  'Lets this person evaluate outside the season plan, during the indoor break or before the season opens. Everyone already chooses their own pillar, so this is only about timing.',
    'staff.swEvaluator':    'Review other staff',
    'staff.swEvaluatorHint':'Write evaluations of other coaches. They see only the ones they wrote, never another reviewer’s.',
    'staff.swOversees':     'Oversee their age groups',
    'staff.swOverseesHint': 'Read every evaluation written for {ages}, by any coach, in a "My ages" tab. Reading only: they still write nothing outside their own teams.',
    'staff.swOverseesNeedsAges': 'Tick Trainer above and choose the age groups first. Those ages are what this person would oversee.',
    'staff.overseesBadge':  'Oversees {ages}',
    'staff.requestSelf':      'Request self-evaluation',
    'staff.requestSelfTitle': 'Who should write a self-evaluation?',
    'staff.requestSelfHint':  'Tick who you want to hear from. They see it in their Coach Hub next time they open the app. Nobody is asked unless ticked, and un-ticking withdraws a request.',
    'staff.requestSelfSend':  'Send to {count}',
    'staff.requestSelfSearch': 'Search staff by name...',
    'staff.requestSelfAll':   'Select these {count}',
    'staff.requestSelfNone':  'Clear',
    'staff.requestSelfSent':  'Asked {count} people for a self-evaluation',
    'staff.requestSelfCleared': 'All self-evaluation requests withdrawn',
    'staff.selfAsked':        'Self-eval asked',
    'staff.selfDone':         'Self-eval in',
    'staff.roleHint':       'Tick what this person does. Trainers also see the trainer-only exercises in the library.',
    'staff.typeAll':        'All',
    'staff.typeCoach':      'Coach',
    'staff.typeTrainer':    'Trainer',
    'staff.typeBoth':       'Coach-Trainer',
    'staff.typeNone':       'No role set',
    'staff.type_coach':     'Coach',
    'staff.type_trainer':   'Trainer',
    'staff.type_both':      'Coach-Trainer',
    'staff.type_none':      'No role set',
    'staff.noneOfType':     'Nobody in this group.',
    'staff.addTitle':       'Add a coach / staff member',
    'staff.addLabel':       'Full name (as the club knows them)',
    'staff.added':          '{name} added',
    'staff.addExists':      'Someone with that name already exists.',
    'staff.rename':         'Rename',
    'staff.renameTitle':    'Display name',
    'staff.merge':          'Merge into...',
    'staff.mergeTitle':     'Merge "{name}" into:',
    'staff.mergeBody':      'All spellings and history of "{from}" will count as "{into}" from now on. This cannot be split apart again from the app.',
    'staff.mergeConfirm':   'Merge',
    'staff.merged':         'Merged into {name}',
    'staff.archive':        'Archive',
    'staff.archived':       'Archived',
    'staff.archiveTitle':   'Archive {name}?',
    'staff.archiveBody':    'Hidden from lists; history stays linked. No data is deleted.',

    // ── Module 1: parent landing page ────────────────────────────────────────
    'parent.gateLink':      'Parent? Request access for your child',
    'parent.title':         'Request player access',
    // No club name here: this screen is public, and the visitor's club is
    // not known until a code is entered.
    'parent.subtitle':      'For parents of players at your club',
    'parent.intro':         'Fill this in once per child. The club reviews every request; your code starts working after approval.',
    'parent.childName':     "Child's full name",
    'parent.childDob':      "Child's date of birth",
    'parent.parentName':    'Your name',
    'parent.parentEmail':   'Your email (only used by the club to verify the request)',
    'parent.submit':        'Request access',
    'parent.missingFields': 'Please fill in your name and your child\'s name.',
    'parent.submitFailed':  'Could not send the request. Please check your connection and try again.',
    'parent.confirmTitle':  'Request received',
    'parent.confirmCode':   'Your personal access code',
    'parent.confirmSave':   'Save this code now - write it down or take a screenshot. It is shown only once.',
    'parent.confirmWhen':   'The code starts working once the club approves your request. The club will not send a notification - just try logging in after a few days.',
    'parent.confirmLink':   'Or save this personal link as a bookmark:',
    'parent.confirmDone':   'Done',
    'parent.copyCode':      'Copy code',
    'parent.copied':        'Copied',

    // ── Module 1: player home + aggregate evaluation view ────────────────────
    // The player always sees ONE combined assessment (the average of all
    // published scores) - never individual, attributable evaluations.
    'player.homeTitle':     'My evaluation',
    'player.greeting':      'Hi {name}',
    'player.unread':        'NEW',
    'player.read':          'Seen',
    'player.empty':         'Nothing published yet. Your evaluation appears here once the club shares it with you.',
    'player.baselineTitle': 'Baseline tests',
    'player.baselineSoon':  'Your fitness and skill test results will appear here once the club shares them.',
    'player.bl.sessionTitle':  'Baseline results',
    'player.bl.sessionIntro':  'Your best result for each test in this session. These are your own numbers - the goal is to beat them next time.',
    'player.bl.attempts':      '{count} attempts',
    'player.bl.noResults':     'No results recorded for you in this session.',
    'player.bl.progressTitle': 'Your progress over time',
    'player.bl.progressSub':   'How your tests are trending',
    'player.bl.progressIntro': 'Each line is one test across your sessions. The line rises as you improve - that trend over time is what matters most.',
    'player.bl.needTwo':       'Need a second session to chart: {names}.',

    // ── Player self-testing (Physical Testing P3, Parkrun model) ─────────────
    'pself.cardTitle':      'Test yourself',
    'pself.cardSub':        'Record your own time on a test',
    'pself.title':          'Test yourself',
    'pself.none':           'No open test sessions right now. Your coach opens one when self-testing is on.',
    'pself.pickSession':    'Pick a test session:',
    'pself.pickTest':       'Pick a test to record:',
    'pself.noTests':        'No tests in this session yet.',
    'pself.otherSession':   'Choose a different session',
    'pself.save':           'Save my time',
    'pself.saved':          'Time recorded',
    'pself.recordedToday':  'Recorded just now',
    'pself.selfNote':       'This is saved as a self-recorded time, kept separate from official tester results.',

    // ── Post-match self-reflection (coach side) ──────────────────────────────
    'reflect.title':          'Game reflections',
    'reflect.forTeam':        'Team {team}',
    'reflect.newTitle':       'Ask your team to reflect',
    'reflect.usesSet':        'Uses the "{set}" questions for this age group.',
    'reflect.fromLineup':     'From a recent line-up (optional)',
    'reflect.matchDate':      'Match date',
    'reflect.opponent':       'Opponent (optional)',
    'reflect.opponentHint':   'e.g. Antwerp',
    'reflect.message':        'Message to the team (optional)',
    'reflect.messageHint':    'A short note that appears above the questions',
    'reflect.open':           'Open reflection',
    'reflect.opened':         'Reflection opened for your team',
    'reflect.needDate':       'Please pick the match date.',
    'reflect.reflectedCount': '{n} reflected so far',
    'reflect.closed':         'closed',
    'reflect.pastTitle':      'Opened reflections',
    'reflect.none':           'No reflections opened yet. Open one above after a game.',
    'reflect.reviewTitle':    'Reflections - {date}',
    'reflect.aPlayer':        'A player',
    'reflect.blank':          'No answers filled in.',
    'reflect.commented':      'Replied',
    'reflect.seen':           'Seen',
    'reflect.newBadge':       'NEW',
    'reflect.yourFeedback':   'Your feedback (optional)',
    'reflect.feedbackHint':   'Write a short reply, or just mark it seen',
    'reflect.saveFeedback':   'Send feedback',
    'reflect.markSeen':       'Just mark seen',
    'reflect.feedbackSaved':  'Feedback sent',
    'reflect.seenToast':      'Marked as seen',
    'reflect.noSubs':         'No one has reflected yet.',
    'reflect.waiting':        'Still to reflect',
    'reflect.close':          'Close reflection',
    'reflect.reopen':         'Reopen',
    'reflect.delete':         'Delete',
    'reflect.deleteTitle':    'Delete this reflection?',
    'reflect.deleteBody':     'This removes the request and every player reply for this game. This cannot be undone.',

    // ── Post-match self-reflection (player side) ─────────────────────────────
    'reflect.p.title':        'Reflect on your game',
    'reflect.p.todoChip':     'NEW',
    'reflect.p.feedbackChip': 'NEW',
    'reflect.p.sentChip':     'Sent',
    'reflect.p.todoSub':      'Your coach asked you to reflect - tap to start',
    'reflect.p.feedbackSub':  'Your coach replied - tap to read',
    'reflect.p.seenSub':      'You have seen your coach\'s reply',
    'reflect.p.sentSub':      'Sent to your coach',
    'reflect.p.optional':     '(optional)',
    'reflect.p.typeHere':     'Type your answer...',
    'reflect.p.submit':       'Send my reflection',
    'reflect.p.incomplete':   'Please answer the questions above before sending.',
    'reflect.p.sent':         'Sent to your coach',
    'reflect.p.mineTitle':    'My reflection',
    'reflect.p.blank':        'No answers filled in.',
    'reflect.p.yourAnswers':  'Your answers',
    'reflect.p.coachTitle':   'From your coach',
    'reflect.p.coachSeen':    'Your coach has seen your reflection.',
    'reflect.p.gotIt':        'Got it',
    'reflect.p.allDone':      'You\'re up to date',
    'reflect.p.edit':         'Edit my answers',
    'player.bl.trend.improving': '▲ Improving',
    'player.bl.trend.declining': '▼ Down on last time - keep at it',
    'player.bl.trend.steady':    '→ Holding steady',
    'player.bl.trend.single':    'First result recorded',
    'player.summaryTitle':  'Your evaluation',
    'player.summaryBasedOn':'Combined from multiple coach assessments',
    'player.summaryUpdated':'Updated {date}',
    'player.summaryIntro':  'These levels are the average of all assessments the club has shared with you - they reflect the whole coaching team\'s view, not one coach\'s opinion.',
    'player.feedbackTitle': 'Feedback from the coaching team',
    'sr.coachTitle':        'For you, from the squad',
    'sr.coachCounts':       '{total} answered · {happy} happy as is',
    'sr.coachHappy':        '{n} said nothing to change.',
    'sr.coachAnon':         'Anonymous: no name was ever stored with these, so they cannot be traced back even in the database.',
    'sr.export':            'Export to spreadsheet',
    'sr.title':             'Match replies',
    'sr.subtitle':          'What the squad sent back after a game',
    'sr.pickTeam':          'Team',
    'sr.empty':             'Nothing sent in yet for this team.',
    'sr.versus':            'v {team}',
    'sr.repliedCount':      '{n} of {total} replied',
    'sr.avgScore':          'average {avg}/5',
    'sr.onTheMatch':        'On the match:',
    'sr.wentWell':          'Went well:',
    'sr.workOn':            'Working on:',
    'sr.praised':           'A teammate said:',
    'sr.notYet':            'Not in yet:',
    'sr.noReply':           'no reply',
    'sr.noneThisMatch':     'No reflections for this match, only teammate notes.',
    'tf.title':             'Team files',
    'tf.subtitle':          'Season schedules and match briefings',
    'tf.pickTeam':          'Team',
    'tf.addTitle':          'Add a file',
    'tf.addHint':           'PDF or PowerPoint, up to 50 MB. Nothing reaches the squad until you publish it, so you can upload on Thursday and release on Saturday.',
    'tf.kindBriefing':      'Match briefing',
    'tf.kindSchedule':      'Season schedule',
    'tf.titlePlaceholder':  'What should the squad see it called?',
    'tf.listTitle':         'Already uploaded',
    'tf.listEmpty':         'Nothing uploaded for this team yet.',
    'tf.live':              'Squad can see it',
    'tf.draft':             'not published',
    'tf.publish':           'Publish',
    'tf.unpublish':         'Withdraw',
    'tf.delete':            'Delete',
    'tf.deleteTitle':       'Delete this file?',
    'tf.deleteBody':        'The squad loses access immediately and the file itself is removed. This cannot be undone.',
    'tf.deleteFailed':      'Could not delete that.',
    'tf.uploading':         'Uploading...',
    'tf.uploaded':          'Uploaded. Publish it when you are ready.',
    'tf.uploadFailed':      'That upload failed. Nothing was added.',
    'tf.tooBig':            'That file is over 50 MB. Compress it or split it up.',
    'share.title':          'Sharing',
    'share.subtitle':       'What reaches families, and how much of it',
    'share.statWritten':    'written',
    'share.statPublished':  'shared',
    'share.statWaiting':    'waiting',
    'share.statNominated':  'with picks',
    'share.formatTitle':    'How much they see',
    'share.formatHint':     'Set this before you publish: it decides what a share actually sends. The club setting applies everywhere; pick an age group to give it something different. A dot means that age has its own setting.',
    'share.scopeClub':      'Whole club',
    'share.modeTopBottom':  'Only strengths and working points',
    'share.modeTopBottomHint': 'Instead of all 25 skills, show just the ones coaches named as best and as needing work. Off = the full picture, as now.',
    'share.swDescriptors':  'Show the written level',
    'share.swDescriptorsHint': 'The sentence under each skill that says what that level looks like.',
    'share.swStrongText':   'Show the coach\'s strong points',
    'share.swStrongTextHint': 'The free text a coach typed, merged across evaluations and never attributed.',
    'share.swWorkingText':  'Show the coach\'s working points',
    'share.swWorkingTextHint': 'The same, for what to work on.',
    'share.clearOverride':  'Clear the {scope} setting and inherit again',
    'share.overrideCleared': 'Back to inheriting',
    'share.nominationGap':  '{n} evaluations have no picks yet. Those players keep seeing the full list until a coach names a best and a weakest.',
    'share.queueTitle':     'Waiting to be shared',
    'share.allTeams':       'All teams',
    'share.queueEmpty':     'No evaluations written yet this season.',
    'share.waitingTag':     '{n} waiting',
    'share.allSharedTag':   'all shared',
    'share.teamCounts':     '{published} of {total} shared · {nominated} with picks',
    'share.andMore':        ' and {n} more',
    'share.publishTeam':    'Share {n}',
    'share.unpublishTeam':  'Withdraw',
    'share.confirmPublishTitle':   'Share with families?',
    'share.confirmUnpublishTitle': 'Withdraw from families?',
    'share.confirmPublish':   'This sends {n} evaluations for {team} to the players and parents who have access. They can read them straight away.',
    'share.confirmUnpublish': 'This hides {n} already shared evaluations for {team} again. Anyone who has read one will have seen it.',
    'share.confirmGo':      'Yes, do it',
    'share.publishedN':     'Shared {n}',
    'share.withdrewN':      'Withdrew {n}',
    'share.publishFailed':  'Could not finish. Nothing else was changed.',
    'share.saveFailed':     'Could not save that setting.',
    'player.strengthsTitle': 'Your strengths',
    'player.workingTitle':   'Your working points',
    'player.strongPoints':  'Strong points',
    'player.workingPoints': 'Working points',
    'player.trLabel':       'In Dutch',
    'player.assessedBy':    'Assessed by: {names}',
    'player.acknowledge':   'Mark as seen',
    'player.allSeen':       'You\'re up to date',
    'player.ackFailed':     'Could not save - try again.',
    'player.signOut':       'Sign out',
    'player.loadFailed':    'Could not load your evaluation. Try again later.',
    'player.downloadReport':'⬇ Download my report (PDF)',
    'player.sharedTitle':   'Shared with you',
    'player.sharedCard':    'Evaluation shared by the club',
    'player.sharedOf':      'Shared evaluation · {date}',

    // ── Module 1: the "before you look at the scores" landing page ──────────
    // The director's message to players and parents. Shown every time on the
    // way into the scores; wording reviewed by the director.
    'pintro.title':         'Before you look at the scores',
    'pintro.continue':      'Show my evaluation →',
    'pintro.p1':            'An evaluation is a feedback moment. It is how our coaches tell you what they saw - what is going well, and where the next step is. It is the start of a conversation, not a verdict.',
    'pintro.p2':            'You or your parents may agree or disagree with parts of it, and that is okay. What you read here is how the coaching team genuinely experienced your play across the period this evaluation covers - not a single match, and not a single opinion.',
    'pintro.p3':            'What we care about most is improvement over time. Hockey is a complex sport: players develop in bursts, different coaches notice different things, and a dip on paper often comes right before a step forward. One evaluation is a snapshot; the pattern across seasons is what matters.',
    'pintro.p4':            'Sharing evaluations like this is new at our club. As more evaluations are collected in one place, season after season, the picture you see here will keep getting more complete and more accurate.',
    'pintro.p5':            'One thing to know: these scores are only part of how the sporting staff looks at development. We also weigh many things that do not translate into a number - and that context stays with the staff, where it guides sporting policy and decisions about selections, team composition and playing style.',
    'pintro.legendTitle':   'What the words mean',
    'pintro.lvl1':          'Building the basics of this skill - every player starts here.',
    'pintro.lvl2':          'Getting it right more and more often; consistency still growing.',
    'pintro.lvl3':          'Reliable at match tempo - you can be counted on for this.',
    'pintro.lvl4':          'Creates advantage with it, even under pressure.',
    'pintro.lvl5':          'Decides moments with it; sets the standard for others.',

    // ── Module 1: director Player Access screen ──────────────────────────────
    'access.title':         'Player access',
    'access.subtitle':      'Parent requests & published evaluations',
    'access.inboxTitle':    'Requests waiting for review',
    'access.inboxEmpty':    'No pending requests.',
    'access.fOpen':         'Waiting',
    'access.fApproved':     'Approved',
    'access.fRejected':     'Rejected',
    'access.fAll':          'All',
    'access.searchPh':      'Search child, parent or email...',
    'access.status_approved': 'approved',
    'access.status_rejected': 'rejected',
    'access.tierId':        'ID match',
    'access.tierSure':      'Name + date of birth',
    'access.tierDob':       'Date of birth only',
    'access.tierName':      'Name only',
    'access.tierNone':      'No match',
    'access.tierChosen':    'You chose',
    'access.flagTaken':     'This child already has approved access. A second parent is fine, but check it is not the same person applying twice.',
    'access.flagShared':    'Another waiting request points at this same child. At most one of them can be right.',
    'access.bulkReady':     '{count} match a player on both name and date of birth, with nothing odd about them.',
    'access.bulkSelect':    'Tick all of those',
    'access.bulkApprove':   'Approve {count} selected',
    'access.bulkWorking':   'Approving...',
    'access.bulkDone':      '{count} approved',
    'access.bulkPartly':    '{done} approved, {failed} could not be done and are still waiting',
    'access.requested':     'requested {date}',
    'access.parentLine':    'Parent: {name}{email}',
    'access.guessMatch':    'Best guess: {name}',
    'access.guessNone':     'No automatic match found',
    'access.choosePlayer':  'Choose player',
    'access.changePlayer':  'Change',
    'access.approve':       'Approve',
    'access.approveNeedsPlayer': 'Pick the player this request belongs to first.',
    'access.approved':      'Approved - code is now active',
    'access.reject':        'Reject',
    'access.rejectTitle':   'Reject this request?',
    'access.rejectBody':    'The code simply never activates. The parent is not notified.',
    'access.rejected':      'Request rejected',
    'access.listTitle':     'Active player access',
    'access.listEmpty':     'No players have access yet. Approve a request above to start.',
    'access.revoke':        'Revoke',
    'access.showCode':      'Show code',
    'access.showCodeTitle': 'Access code for {name}',
    'access.showCodeBody':  'Send this to the parent if they lost it. It is the same code, still active.',
    'access.copyCode':      'Copy code',
    'access.codeCopied':    'Code copied',
    'access.revokeTitle':   'Revoke access for {name}?',
    'access.revokeBody':    'Their code stops working immediately. Published evaluations stay published if you re-approve later.',
    'access.revoked':       'Access revoked',
    'access.evalsFor':      'Evaluations',
    'access.publish':       'Publish',
    'access.unpublish':     'Unpublish',
    'access.shareOne':      'Share 1-on-1',
    'access.unshareOne':    'Unshare 1-on-1',
    'access.translate':     'Translate',
    'access.trDone':        'Dutch ready',
    'access.trTitle':       'Translate feedback to Dutch',
    'access.trHint':        'Generate an automatic Dutch translation, then edit it if needed. The player sees your original English AND this Dutch together.',
    'access.trGen':         'Generate Dutch',
    'access.trGenBusy':     'Translating...',
    'access.trNoText':      'This evaluation has no strong / working points to translate.',
    'access.trFailed':      'Translation failed - try again.',
    'access.trSaved':       'Dutch translation saved',
    'access.sharedTag':     'shared 1-on-1',
    'access.ackRead':       'seen {date}',
    'access.ackUnread':     'not seen yet',
    'access.neverPublished':'not published',
    'access.importedTag':   'imported - never shown to players',
    'access.publishBlock':      'Publish a block...',
    'access.publishBlockTitle': 'Publish a fortnight block',
    'access.publishBlockHint':  'Each block is two weeks of evaluations. Publishing makes every evaluation in the block visible to its player (once the player has access). Imports are never included.',
    'access.publishBlockCount': '{evals} evaluations · {players} players · {teams} teams',
    'access.publishBlockConfirmTitle': 'Publish this block?',
    'access.publishBlockConfirmBody':  '{evals} evaluations for {players} players across {teams} teams become visible to their players. You can unpublish any single evaluation afterwards.',
    'access.publishBlockGo':    'Publish block',
    'access.publishBlockDone':  'Block published: {count} evaluations',
    'access.publishBlockNone':  'Nothing unpublished this season - all caught up.',
    'access.publishLatestTeam': 'Publish latest for a team...',
    'access.publishLatestTitle':'Publish latest evaluations',
    'access.publishLatestBody': 'For every player with access in {team}, their most recent evaluation is published. Already-published ones are left as they are.',
    'access.publishLatestDone': 'Published {count} evaluation(s)',
    'access.publishLatestNone': 'Nothing new to publish for that team.',
    'access.searchPlayer':  'Type to search players...',

    // ── Module 6: staff codes (director, Coaches screen) ─────────────────────
    'shub.codeIssue':       'Issue code',
    'shub.codeActive':      'Code active',
    'shub.codeShow':        'Show code',
    'shub.codeRevoke':      'Revoke code',
    'shub.codeIssuedTitle': 'Staff code for {name}',
    'shub.codeIssuedBody':  'Share this code personally (e.g. WhatsApp). It is their login for the Coach Hub.',
    'shub.codeCopy':        'Copy code',
    'shub.codeCopied':      'Copied',
    'shub.codeRevokeTitle': 'Revoke {name}\'s code?',
    'shub.codeRevokeBody':  'They are signed out at their next visit and cannot log back in until you issue a new code.',
    'shub.codeRevoked':     'Code revoked',
    'shub.evaluate':        'Evaluations',
    'shub.notes':           'Notes',
    'shub.teams':           'Teams ({count})',
    'shub.teamsTitle':      'Teams {name} evaluates',
    'shub.teamsHint':       'Pick the team(s) this coach evaluates with their personal code, and the Season Plan that sets their season dates and suggested pillar.',
    'shub.teamsPlan':       'Season Plan (pillar rotation)',
    'shub.teamsNoPlan':     'No plan - all pillars always open',
    'shub.teamsPick':       'Teams this coach evaluates',
    'shub.trainerOn':       '◆ Trainer',
    'shub.trainerOff':      'Make trainer',
    'shub.trainerGranted':  '{name} can now see the trainer section of the library (from their next login)',
    'shub.trainerRemoved':  '{name} no longer sees the trainer section (from their next login)',
    'shub.trainerTitle':    'Trainer access for {name}',
    'shub.trainerHint':     'Pick the age groups whose trainer exercises this person can see. None selected = not a trainer.',

    // ── Coach/trainer self-apply (public apply + director inbox) ──────────────
    'staffApply.gateLink':  'Coach or trainer? Apply for access',
    'staffApply.title':     'Apply for access',
    'staffApply.subtitle':  'Coaches & trainers',
    'staffApply.intro':     'Fill this in and the club will review it. You will get a code that works once the director approves you.',
    'staffApply.name':      'Your full name',
    'staffApply.contact':   'Email or phone (so the club can reach you)',
    'staffApply.teamNote':  'Which team(s) do you help with?',
    'staffApply.submit':    'Send application',
    'staffApply.missingName':'Please enter your name.',
    'staffApply.submitFailed':'Could not send. Please try again.',
    'staffApply.confirmTitle':'Application sent',
    'staffApply.confirmCode':'Your code (save it):',
    'staffApply.confirmSave':'Write this code down now. It is your login once the director approves you.',
    'staffApply.confirmWhen':'It will not work until the club approves your application.',
    'staffApply.confirmDone':'Done',
    'staffApply.copyCode':  'Copy code',
    'staffApply.copied':    'Copied',

    'staffAccess.title':    'Staff access',
    'staffAccess.subtitle': 'Coach & trainer applications',
    'staffAccess.loadFailed':'Could not load applications.',
    'staffAccess.pending':  'Waiting for you',
    'staffAccess.searchPh': 'Search name, contact or team...',
    'staffAccess.handled':  'Handled',
    'staffAccess.empty':    'No new applications.',
    'staffAccess.requested':'applied {date}',
    'staffAccess.approve':  'Approve',
    'staffAccess.reject':   'Reject',
    'staffAccess.status_approved':'approved',
    'staffAccess.status_rejected':'rejected',
    'staffAccess.status_revoked': 'revoked',
    'staffAccess.lapsed':   'off the roster',
    'staffAccess.approveTitle':'Approve {name}',
    'staffAccess.saidTeam': 'They said: {note}',
    'staffAccess.dupWarning': '{name} is already on your staff list. Approving makes a second, separate person with their own login. If this is the same person, cancel and give them the existing code instead.',
    'staffAccess.showCode':  'Show code',
    'staffAccess.codeTitle': 'Login code for {name}',
    'staffAccess.codeBody':  'This is the code they entered the app with. Send it to them again if they lost it. It keeps working until you revoke it on the Staff screen.',
    'staffAccess.codeCopy':  'Copy code',
    'staffAccess.codeCopied': 'Code copied',
    'staffAccess.staffName':'Name on the roster',
    'staffAccess.assignTeam':'Assign teams (optional)',
    'staffAccess.assignTeamHint':'Tap the team(s) this coach helps with. You can add more later from the roster.',
    'staffAccess.plan':     'Season plan',
    'staffAccess.planHint': 'Sets the season dates and breaks for the teams above, and suggests a pillar each fortnight. Coaches choose which pillar they evaluate; the plan decides when evaluating is open at all.',
    'staffAccess.noPlan':   'No plan (all pillars open)',
    'staffAccess.trainerAges':'Trainer age groups (optional)',
    'staffAccess.trainerAgesHint':'Pick ages to make them a trainer who sees those trainer exercises. Leave empty for a coach.',
    'staffAccess.approved': '{name} approved. Their code now works.',
    'staffAccess.rejectTitle':'Reject {name}?',
    'staffAccess.rejectBody':'Their code will never activate. You can still add them manually later.',
    'staffAccess.rejected': 'Application rejected',

    // ── Module 6: evaluator side ─────────────────────────────────────────────
    'seval.listTitle':      'Evaluations - {name}',
    'seval.new':            '+ New evaluation',
    'seval.none':           'No evaluations yet for this person.',
    'seval.statusDraft':    'draft',
    'seval.statusLocked':   'locked',
    'seval.statusPublished':'published',
    'seval.statusAcked':    'seen {date}',
    'seval.editTitle':      'Evaluate {name}',
    'seval.header':         'Header',
    'seval.role':           'Role(s) being assessed',
    'seval.roleTrainer':    'Trainer (training sessions)',
    'seval.roleCoach':      'Coach (match day)',
    'seval.roleBoth':       'Both',
    'seval.team':           'Team',
    'seval.ageCategory':    'Age category',
    'seval.season':         'Season',
    'seval.date':           'Date of evaluation',
    'seval.compliance':     'Qualification & compliance',
    'seval.diploma':        'Coaching diploma held',
    'seval.diplomaReq':     "Meets diploma requirement for this team's division",
    'seval.reqYes':         'Yes',
    'seval.reqNo':          'No',
    'seval.reqNotRequired': 'Not required',
    'seval.reqInProgress':  'In progress',
    'seval.safeguarding':   'Safeguarding / code of conduct signed',
    'seval.sgOnFile':       'Yes, on file',
    'seval.sgFollowUp':     'No / to follow up',
    'seval.addComment':     'Comment',
    'seval.partD':          'Part D - self-reflection (written by {name})',
    'seval.partDEmpty':     'Not filled in yet. {name} can write this from their Coach Hub until you lock the evaluation.',
    'seval.partDWell':      'What went well this season / period?',
    'seval.partDImprove':   'What would I like to do differently or improve?',
    'seval.partDSupport':   'Where would I like support from the club?',
    'seval.partE':          'Part E - overall evaluation',
    'seval.progressPrev':   "Progress on previous review's goals",
    'seval.noPrev':         'No previous review',
    'seval.prevActions':    "Previous review's action points",
    'seval.strengths':      'Strengths',
    'seval.development':    'Development points / goals',
    'seval.actions':        'Concrete agreements / action points',
    'seval.addAction':      '+ Add action point',
    'seval.actionDone':     'Done',
    'seval.actionReopen':   'Reopen',
    'seval.sinceLast':      'Feedback notes since the previous review (sent + private)',
    'seval.sinceLastNone':  'No notes recorded since the previous review.',
    'seval.lock':           'Lock (freeze self-reflection)',
    'seval.unlock':         'Unlock',
    'seval.lockedHint':     'Locked - the self-reflection is frozen. Publish when ready.',
    'seval.publish':        'Publish to {name}',
    'seval.unpublish':      'Unpublish',
    'seval.publishedHint':  'Published - {name} can read this evaluation in their Coach Hub.',
    'seval.publishNeedsLock':'Lock the evaluation before publishing.',
    'seval.delete':         'Delete evaluation',
    'seval.deleteTitle':    'Delete this draft?',
    'seval.deleteBody':     'All ratings and action points in it are removed. This cannot be undone.',
    'seval.progress':       '{done} of {total} criteria rated',
    'seval.ratedNA':        'N/A',
    'seval.sectionsTitle':  'Rating sections',
    'seval.sectionsHint':   'Pick the part you are evaluating. Your taps save instantly - come back any time.',
    'seval.backToSections': 'All sections',
    'seval.naWord':         'Not applicable',

    // ── Module 6: staff-side Coach Hub ───────────────────────────────────────
    'chub.title':           'Coach Hub',
    'chub.greeting':        'Hi {name}',
    'chub.tabEvals':        'Evaluations',
    'chub.tabFeedback':     'Feedback',
    'chub.tabLibrary':      'Library',
    'chub.tabTraining':     'Training',
    'chub.tabAges':         'My ages',
    'chub.agesIntro':       'The age groups you oversee. You can read every evaluation here; only the coach who wrote one can change it.',
    'chub.agesSearchPh':    'Search player, team or coach...',
    'chub.agesEmpty':       'Nothing has been written in your age groups yet.',
    'chub.agesHeadline':    '{done} of {total} children evaluated',
    'chub.agesHeadlineSub': '{waiting} still waiting for their first evaluation this season',
    'chub.agesTeamLine':    '{done} of {total} evaluated',
    'chub.agesLast':        'last {date}',
    'chub.agesNever':       'nothing yet',
    'chub.agesWaiting':     'Still waiting',
    'chub.agesAllDone':     'Everyone in this team has been evaluated.',
    'chub.agesRecent':      'Most recent',
    'chub.agesUnknownPlayer': 'Unknown player',
    'chub.agesStrong':      'Strong points',
    'chub.agesWorking':     'Working points',
    'chub.agesNotes':       'Notes',
    'home.noPlayersYet':    'No players in any team yet. Import your roster and the teams will appear here.',
    'home.searchTeam':      'Search a team',
    'home.noTeamMatch':     'No team matches "{q}".',
    'home.lastWorkedOn':    'Last worked on',
    'chub.reflectionAsk':   'Your input requested',
    'chub.reflectionHint':  'Fill in your self-reflection before the evaluation meeting - it makes the review a two-way conversation.',
    'chub.reflectionOpen':  'Write self-reflection',
    'chub.reflectionSaved': 'Self-reflection saved',
    'chub.reflectionTitle': 'Self-reflection',
    'chub.reflectionFor':   'For the evaluation dated {date} ({evaluator})',
    'chub.save':            'Save',
    'chub.evalsNone':       'No published evaluations yet. Once the club shares your evaluation, it appears here.',
    'chub.evalOf':          'Evaluation of {date}',
    'chub.ackBtn':          'Mark as seen',
    'chub.ackDone':         'Seen on {date}',
    'chub.feedbackNone':    'No feedback notes yet.',
    'chub.markSeen':        'Mark as seen',
    'chub.seen':            'Seen',
    'chub.librarySoon':     'Club resources (warm-ups, exercises, focus documents) appear here soon.',
    'chub.signOut':         'Sign out',
    'chub.loadFailed':      'Could not load. Try again later.',
    'chub.evalPlayers':     'Evaluate my players',
    'chub.evalPlayersOne':  '{team} · choose a pillar',
    'chub.evalPlayersMany': '{count} teams · choose a pillar',
    'chub.pickTeam':        'Which team?',
    'chub.baseline':        'Baseline testing',
    'chub.baselineOne':     '{name}',
    'chub.baselineMany':    '{count} sessions to test',
    'chub.teamTest':        'Test my team',
    'chub.teamTestSub':     'Run a fitness or skill test yourself, no setup needed',
    'chub.teamTestOpen':    '{count} test(s) open',
    'chub.teamTestNew':     '+ New test',
    'chub.baselinePick':    'Which session?',
    'chub.lineups':         'Line-ups',
    'chub.lineupsSub':      'Record match line-ups & see who plays',
    'chub.gamePicks':       'After the game',
    'chub.gamePicksSub':    'Your key and weak player picks from a match',
    'chub.reflect':         'Game reflections',
    'chub.reflectSub':      'Ask your team to reflect on a game & read their replies',
    'lu.title':             'Line-ups',
    'lu.new':               '+ New line-up',
    'lu.newTitle':          'New line-up',
    'lu.matches':           'Matches',
    'lu.noneYet':           'No line-ups yet. Tap "New line-up" to start.',
    'lu.date':              'Match date',
    'lu.opponent':          'Opponent (optional)',
    'lu.opponentPh':        'e.g. Antwerp',
    'lu.formation':         'Formation',
    'lu.participation':     'Participation this season',
    'lu.player':            'Player',
    'lu.started':           'Start',
    'lu.sub':               'Sub',
    'lu.out':               'Out',
    'lu.mainRole':          'Main role',
    'lu.editHint':          'Tap a spot to place a player. {done} of {total} placed.',
    'lu.subs':              'Substitutes',
    'lu.subsHint':          'For each substitute, tap the position(s) they can play. They appear in the substitutes box on the shared image.',
    'lu.benchEmpty':        'Everyone is on the field.',
    'lu.download':          'Download image',
    'lu.share':             'Share',
    'lu.pickPlayer':        'Who plays here?',
    'lu.allPlaced':         'Every player is already placed.',
    'lu.fieldFull':         'The field is full - remove someone first.',
    'lu.save':              'Save line-up',
    'lu.saved':             'Line-up saved',
    'lu.delete':            'Delete',
    'lu.deleteTitle':       'Delete this line-up?',
    'lu.deleteBody':        'The line-up and its participation record are removed. This cannot be undone.',
    'chub.pillarPickTitle': 'Which pillar today?',
    'chub.pillarPickHint':  'Pick what you are evaluating {team} on. You will rate every player on this pillar, then you can come back and do another.',
    'chub.pillarSuggested': 'Suggested',
    'chub.pillarAll':       'All pillars',
    'chub.pillarAllHint':   'Longer: a full evaluation',
    'chub.pillarNow':       'Evaluating: {pillar}',
    'chub.windowTitle':     'This fortnight',
    'chub.windowOpen':      'Open pillar: {pillar}',
    'chub.windowExcluded':  'No pillar is scheduled for {team} this fortnight. Enjoy the break - the next one opens automatically.',
    'chub.windowBefore':    'The season plan hasn\'t started yet. Evaluations open on the first scheduled window.',
    'chub.windowAfter':     'The season plan has finished for this year. Thanks for all the evaluations!',
    'chub.windowClosed':    'No evaluation window is open right now.',
    'chub.windowBreak':     'Evaluations are paused for the indoor season. They start again when the outdoor calendar resumes, picking up at the next pillar.',

    // ── Module 6: feedback notes (director composer) ─────────────────────────
    'snote.title':          'Feedback notes - {name}',
    'snote.catMatch':       'Match',
    'snote.catTraining':    'Training',
    'snote.catFocus':       'Focus point',
    'snote.catGeneral':     'General',
    'snote.placeholder':    'Two sentences is plenty...',
    'snote.send':           'Send',
    'snote.private':        'Private',
    'snote.sent':           'Sent to {name}',
    'snote.savedPrivate':   'Saved (private)',
    'snote.emptyBody':      'Write the note first.',
    'snote.none':           'No notes yet for this person.',
    'snote.privateTag':     'PRIVATE',
    'snote.seenTag':        'seen {date}',
    'snote.unseenTag':      'not seen yet',
    'snote.deleteTitle':    'Delete this note?',
    'snote.deleteBody':     'It disappears from your record and from the coach\'s feed if it was sent.',

    // ── Module 6: DTA Library ────────────────────────────────────────────────
    'dta.title':            'DTA Library',
    'dta.subtitle':         'Club resources for coaches',
    'dta.add':              '+ Add resource',
    'dta.typePdf':          'PDF upload',
    'dta.typeArticle':      'Article (written in-app)',
    'dta.typeLink':         'Link (e.g. drill video)',
    'dta.catWarmup':        'Warm-up',
    'dta.catTechnical':     'Technical',
    'dta.catTactical':      'Tactical',
    'dta.catPhysical':      'Physical',
    'dta.catMental':        'Mental',
    'dta.catSocial':        'Social',
    'dta.catClub':          'Club info',
    'dta.allCats':          'All',
    'dta.allAges':          'All ages',
    'dta.fieldTitle':       'Title',
    'dta.fieldBody':        'Article text',
    'dta.bodyPlaceholder':  'Write the session here. Use the buttons above for headings, bold, lists and quotes.',
    'dta.previewLabel':     'Preview (what coaches see)',
    'dta.previewEmpty':     'Your formatted article will appear here as you type.',
    'dta.mdHeading':        'Heading',
    'dta.mdBold':           'Bold',
    'dta.mdItalic':         'Italic',
    'dta.mdBullet':         'Bullet list',
    'dta.mdNumber':         'Numbered list',
    'dta.mdQuote':          'Quote',
    // Figures manager (multiple inline figures per article)
    'dta.figures':          'Figures (diagrams & images)',
    'dta.addDiagram':       '+ Diagram',
    'dta.addField':         '+ Build field',
    'dta.addImage':         '+ Image',
    'dta.figCaption':       'Caption (optional)',
    'dta.figInsert':        'Insert in text',
    'dta.figEdit':          'Edit',
    'dta.figChange':        'Change',
    'dta.figReplace':       'Replace',
    'dta.figRemove':        'Remove',
    'dta.figUnplaced':      'not placed yet',
    'dta.figEmpty':         'No figures yet. Add a diagram, build a field, or upload an image, then insert it where you want it in the text.',
    'dta.figDiagram':       'Diagram',
    'dta.figField':         'Field diagram',
    'dta.figImage':         'Image',
    'dta.pickDiagram':      'Pick a diagram',
    // Field-diagram builder (drag-and-drop pitch)
    'dfx.title':            'Field diagram',
    'dfx.view.full':        'Full field',
    'dfx.view.mid':         'Midfield',
    'dfx.view.d':           'Circle (D)',
    'dfx.view.quarter':     'Corner',
    'dfx.view.grid':        'Training box',
    'dfx.view.square':      'Small grid',
    'dfx.player':           '+ Player',
    'dfx.opp':              '+ Opponent',
    'dfx.ball':             '+ Ball',
    'dfx.run':              '+ Run',
    'dfx.pass':             '+ Pass',
    'dfx.zone':             '+ Zone',
    'dfx.text':             '+ Label',
    'dfx.hint':             'Tap a button to drop it on the pitch, then drag to move. Tap an item to select it, edit its label, colour or delete. Drag the small middle dot of an arrow to curve it.',
    'dfx.selectHint':       'Tap an item on the pitch to edit it.',
    'dfx.labelPh':          'A-Z / 0-9',
    'dfx.textPh':           'Label text',
    'dfx.textDefault':      'Label',
    'dfx.gold':             'Our player',
    'dfx.red':              'Highlighted',
    'dfx.colour':           'Colour',
    'dfx.duplicate':        'Duplicate',
    'dfx.delete':           'Delete',
    'dfx.straighten':       'Straighten',
    'dfx.clear':            'Clear all',
    'dfx.save':             'Save diagram',
    'dta.fieldUrl':         'Link (https://...)',
    'dta.fieldFile':        'PDF file (max 10 MB)',
    'dta.fieldAges':        'Age groups (leave empty = everyone)',
    'dta.bulkAdd':          'Add several PDFs',
    'dta.bulkTitle':        'Add several PDFs at once',
    'dta.bulkHint':         'Pick as many PDFs as you like. Each one becomes its own library entry, named after its file. The category, audience and ages below apply to all of them.',
    'dta.bulkUpload':       'Upload {n}',
    'dta.bulkUploading':    'Uploading...',
    'dta.bulkDone':         'Added',
    'dta.bulkFailed':       'Failed',
    'dta.bulkNeedsTitle':   'Every file needs a title.',
    'dta.bulkAllDone':      'Added {n} to the library',
    'dta.bulkPartly':       '{ok} added, {bad} failed. The failed ones are still listed, press Upload to retry them.',
    'dta.fieldTitleNl':     'Title (Dutch)',
    'dta.fieldBodyNl':      'Article text (Dutch)',
    'dta.previewLabelNl':   'Preview (Dutch)',
    'dta.nlFallback':       'Leave blank to show the English version',
    'dta.fieldAudience':    'Who can see this',
    'dta.audAll':           'Everyone',
    'dta.audTrainers':      'Trainers only',
    'dta.searchPh':         'Search articles...',
    'dta.fieldCategory':    'Category',
    'dta.allCatsLong':      'All categories',
    'dta.allStatus':        'All statuses',
    'dta.allAudiences':     'Everyone + trainers',
    'dta.allAgesShort':     'All ages',
    'dta.filterStatus':     'Publish status',
    'dta.showing':          'Showing {shown} of {total}',
    'dta.viewLabel':        'View',
    'dta.viewList':         'List view',
    'dta.viewGrid':         'Card view',
    'dta.colTitle':         'Title',
    'dta.colAges':          'Ages',
    'dta.colStatus':        'Status',
    'dta.colViews':         'Opens',
    'dta.preview':          'Preview',
    'dta.planPdf':          'PDF',
    'dta.previewNote':      'This is how a coach sees it. Nothing is published or recorded.',
    'dta.trainerSection':   'Trainer exercises',
    'dta.dailyPick':        'Article of the day',
    'dta.noResults':        'Nothing matches your search.',

    // ── Season plan practices ────────────────────────────────────────────────
    'prac.thisWeek':        'This week in the season plan',
    'prac.weekOne':         '{band} · week {n}',
    'prac.weekMany':        'This week for your {count} age groups',
    'prac.week':            'Week {n}',
    'prac.now':             'This week',
    'prac.reference':       'Reference',
    'prac.allBlocks':       'Whole season',
    'prac.searchPh':        'Search practices...',
    'prac.exportPdf':       'Download the whole season plan (PDF)',
    'prac.time':            'Time',
    'prac.area':            'Area',
    'prac.players':         'Players',
    'prac.kit':             'Kit',
    'prac.setup':           'Set up',
    'prac.organisation':    'How it runs',
    'prac.points':          'Coaching points',
    'prac.cues':            'Cues',
    'prac.detail':          'Why this practice',
    'prac.progression':     'Progression',
    'prac.good':            'What good looks like',
    'prac.adapt':           'Make it easier or harder',
    'prac.easier':          'Easier',
    'prac.harder':          'Harder',
    'prac.questions':       'Ask the players',
    'prac.link':            'Links to',
    'prac.drillsFor':       'Practices for this',
    'prac.drillsTitle':     'Practices for {skill}',
    'prac.drillsSub':       '{count} from the season plan for this age, most focused first.',
    'prac.drillsNone':      'No practice in this age plan trains {skill} yet.',

    'ex.section':        'From our coaches',
    'ex.sectionEmpty':   'Nobody has shared an exercise yet. Yours could be the first.',
    'ex.newBtn':         '+ Write an exercise',
    'ex.newTitle':       'New exercise',
    'ex.editTitle':      'Edit exercise',
    'ex.editSub':        'Shared with every coach in the club',
    'ex.byLine':         'by {name}',
    'ex.minutes':        '{n} min',
    'ex.state_draft':    'Draft',
    'ex.state_hidden':   'Taken down',
    'ex.state_deleted':  'Withdrawn by the author',
    'ex.state_published': 'Shared',
    'ex.aim':            'What it is for',
    'ex.edit':           'Edit',
    'ex.fTitle':         'Name of the exercise',
    'ex.fTitlePh':       'e.g. Four goal game',
    'ex.fAges':          'Which ages is it for?',
    'ex.fCategory':      'Pillar',
    'ex.fMinutes':       'Minutes',
    'ex.fPlayers':       'Players',
    'ex.fPlayersPh':     'e.g. 8 to 12',
    'ex.fAim':           'What is it for?',
    'ex.fAimPh':         'One or two lines on what this practice trains.',
    'ex.fOrganisation':  'How it runs',
    'ex.fOrganisationPh': 'Two teams of four in a 30 x 20 area.\nScore in either of the two small goals.',
    'ex.fPoints':        'Coaching points',
    'ex.fPointsPh':      'Head up before receiving\nFirst touch into space',
    'ex.fKit':           'Kit',
    'ex.fKitPh':         'e.g. 8 cones, 4 balls, 2 mini goals',
    'ex.fLineHint':      'One per line.',
    'ex.fDiagram':       'Diagram (optional)',
    'ex.fDiagramHint':   'Draw the set-up on a pitch. Plenty of good exercises need no picture at all.',
    'ex.draw':           'Draw a diagram',
    'ex.redraw':         'Edit the diagram',
    'ex.removeDiagram':  'Remove diagram',
    'ex.todo':           'Before you can share this:',
    'ex.p_title_required': 'Give it a name',
    'ex.p_organisation_required': 'Say how it runs',
    'ex.p_points_required': 'Add at least one coaching point',
    'ex.p_age_required': 'Pick at least one age group',
    'ex.p_title_too_long': 'The name is too long',
    'ex.p_too_many_organisation': 'Too many lines under how it runs',
    'ex.p_too_many_points': 'Too many coaching points',
    'ex.p_organisation_line_too_long': 'One of the lines is too long',
    'ex.p_aim_too_long': 'What it is for is too long',
    'ex.p_kit_too_long': 'The kit list is too long',
    'ex.p_minutes_out_of_range': 'Minutes should be between 1 and 120',
    'ex.saveDraft':      'Save as draft',
    'ex.publish':        'Share with the club',
    'ex.saveLive':       'Save changes',
    'ex.publishNote':    'Sharing puts it straight in the library for every coach. You can edit or delete it afterwards.',
    'ex.notReady':       'A few things still to fill in.',
    'ex.published':      'Shared with the club',
    'ex.savedDraft':     'Saved as a draft, only you can see it',
    'ex.delete':         'Delete this exercise',
    'ex.deleteTitle':    'Delete this exercise?',
    'ex.deleteBody':     'It disappears from the library for everyone, including you. The club keeps a copy of anything that has been saved, so nothing written for the club is ever lost.',
    'ex.deleted':        'Exercise deleted',

    'dta.exSection':      'Exercises from coaches',
    'dta.exSectionHint':  'Coaches share these themselves, without asking first. Everything they save is kept here, including drafts and anything they later withdrew, so nothing written for the club is lost. Taking one down hides it from every coach straight away and you can put it back.',
    'dta.exTakeDown':     'Take down',
    'dta.exRestore':      'Put back',
    'dta.exTakeDownTitle': 'Take down "{name}"?',
    'dta.exTakeDownBody': 'It disappears from every coach\u2019s library immediately. {name} keeps their copy and you can put it back at any time.',
    'dta.exRestoreTitle': 'Put this back in the library?',
    'dta.exRestoreBody':  'Every coach will see it again.',
    'dta.exTakenDown':    'Taken down',
    'dta.exRestored':     'Back in the library',

    'dta.seasonPlans':      'Season plans',
    'dta.seasonPlansHint':  'Publishing makes a plan available, it does not hand it out. Only staff you have given the season plan to on the Staff screen can see it, and for them this week’s practice lands on their home screen.',
    'dta.planWeeks':        '{weeks} weeks + reference, {total} practices',
    'dta.planPartly':       '{live} of {total} live',
    'dta.planPublishTitle': 'Publish the {band} season plan?',
    'dta.planPublishBody':  'All {count} practices become available to the staff of that age who have the season plan switched on. Nobody else sees them.',
    'dta.planUnpublishTitle': 'Unpublish the {band} season plan?',
    'dta.planUnpublishBody':  'All {count} practices disappear from the coaches’ Training tab.',
    'dta.planPublished':    '{band} plan published',
    'dta.planUnpublished':  '{band} plan unpublished',

    'dta.illoBadType':      'Please choose a PNG, JPG or WebP image.',
    'dta.pinned':           'Pinned',
    'dta.pin':              'Pin',
    'dta.unpin':            'Unpin',
    'dta.publish':          'Publish',
    'dta.unpublish':        'Unpublish',
    'dta.publishedTag':     'published',
    'dta.draftTag':         'draft',
    'dta.replaceFile':      'Replace file',
    'dta.edit':             'Edit',
    'dta.delete':           'Delete',
    'dta.deleteTitle':      'Delete "{title}"?',
    'dta.deleteBody':       'Coaches lose access immediately. An uploaded PDF is removed from storage too.',
    'dta.save':             'Save',
    'dta.saved':            'Saved',
    'dta.invalidUrl':       'That does not look like a valid web address.',
    'dta.missingTitle':     'Give it a title first.',
    'dta.missingFile':      'Pick a PDF file first.',
    'dta.fileTooBig':       'That file is over 10 MB. Compress it and try again.',
    'dta.notPdf':           'Only PDF files can be uploaded.',
    'dta.uploading':        'Uploading...',
    'dta.viewsLine':        'Opened by {count} coach(es)',
    'dta.viewsNone':        'Not opened yet',
    'dta.empty':            'No resources yet. Add the standard warm-up routine first - pin it so it stays on top.',
    'dta.coachEmpty':       'No resources published yet. Warm-ups, exercises and club documents will appear here.',
    'dta.openPdf':          'Open PDF',
    'dta.openLink':         'Open link',
    'dta.read':             'Read',
    'dta.loadFailed':       'Could not load the library. Try again later.',
    'dta.pdfFailed':        'Could not open the PDF. Check your connection and try again.',

    // ── Module 2: Baseline Testing (director) ────────────────────────────────
    'bl.title':             'Baseline testing',
    'bl.subtitle':          'Field-test sessions',
    'bl.new':               '+ New session',
    'bl.empty':             'No baseline sessions yet. Create one to schedule field tests and hand out tester codes.',
    'bl.statusOpen':        'open',
    'bl.statusClosed':      'closed',
    'bl.published':         'published',
    'bl.wizTitle':          'New baseline session',
    'bl.step':              'Step {n} of 4',
    'bl.stepBasics':        'Basics',
    'bl.stepExercises':     'Exercises',
    'bl.stepPlayers':       'Players',
    'bl.stepTesters':       'Tester codes',
    'bl.basicsName':        'Session name',
    'bl.basicsNamePh':      'e.g. U14 baseline - September',
    'bl.basicsDate':        'Date',
    'bl.selfTestLabel':     'Player self-testing',
    'bl.selfTestOn':        'On - players can record their own times',
    'bl.selfTestOff':       'Off - official testers only',
    'bl.selfTestHint':      'When on, every player in this session sees it under "Test yourself" while the session is open. Self-recorded times stay separate from official results.',
    'bl.exercisesHint':     'Pick the exercises this session will run.',
    'bl.playersHint':       'Add whole teams or individual players.',
    'bl.playersAddTeam':    'Add a team',
    'bl.playersAddIndividual': 'Add individual players',
    'bl.playersSearch':     'Search a player by name...',
    'bl.playersChosen':     '{count} player(s) selected',
    'bl.newTest':           '+ New test',
    'bl.newTestTitle':      'Create a test',
    'bl.newTestName':       'Test name',
    'bl.newTestNamePh':     'e.g. 1 km time trial',
    'bl.newTestType':       'How is it measured?',
    'bl.newTestTypeStopwatch': 'Stopwatch (a time)',
    'bl.newTestTypeReps':   'Count (reps / points)',
    'bl.newTestTypeDistance': 'Distance / number',
    'bl.newTestBetter':     'Which is better?',
    'bl.newTestBetterMin':  'Lower is better (e.g. a time)',
    'bl.newTestBetterMax':  'Higher is better (e.g. reps)',
    'bl.newTestTags':       'Tags (optional, comma separated)',
    'bl.newTestTagsPh':     'e.g. endurance, speed',
    'bl.newTestCreate':     'Create test',
    'bl.newTestNeedName':   'Give the test a name.',
    'bl.newTestExists':     'That test already exists - selected it.',
    'bl.newTestCreated':    'Test created and selected.',
    'bl.playersExternals':  'Guests added',
    'bl.externalTag':       'guest',
    'bl.externalNoMatch':   'No players match. You can add this name as a guest.',
    'bl.results.sets':      'Sets',
    'bl.results.fade':      'fade {pct}%',
    'bl.externalAdd':       'Add "{name}" as a guest',
    'bl.externalNeedName':  'Type a name first.',
    'bl.testersHint':       'Add one code per tester (their name is baked in). Share codes personally - they log in with them pitch-side.',
    'bl.testerName':        'Tester name',
    'bl.testerAdd':         '+ Add tester',
    'bl.testerAddTitle':    'Add a tester',
    'bl.testerFromStaff':   'Assign a coach / trainer',
    'bl.testerOrName':      'Or add a one-off name',
    'bl.next':              'Next →',
    'bl.back':              '← Back',
    'bl.create':            'Create session',
    'bl.createdToast':      'Session created',
    'bl.needName':          'Give the session a name.',
    'bl.needExercise':      'Pick at least one exercise.',
    'bl.needPlayer':        'Add at least one player.',
    'bl.needTester':        'Add at least one tester code.',
    // Detail
    'bl.detailExercises':   'Exercises',
    'bl.detailPlayers':     'Players',
    'bl.detailTesters':     'Tester codes',
    'bl.detailProgress':    'Progress',
    'bl.progressLine':      '{done} of {total} tested',
    'bl.viewResults':       '📊 View results',
    'bl.results.legend':    'Best result per player, fastest/highest first. Dots compare to the team (T) and age-group (A) average - gold is better, red is below.',
    'bl.results.none':      'No results recorded yet.',
    'blpdf.download':       '⬇️ Download PDF',
    'blpdf.title':          'Baseline PDF',
    'blpdf.layout':         'Layout',
    'blpdf.summary':        'Best results summary',
    'blpdf.history':        'Full history (all attempts)',
    'blpdf.dots':           'Show team (T) & age-group (A) comparison dots',
    'blpdf.generate':       'Generate PDF',
    'blpdf.legend':         'Dots compare to the team (T) and age-group (A) average: gold = better, red = below, navy = around average.',
    'blpdf.attempts':       'Attempts',
    'blpdf.external':       'External / no team',
    'blillu.howMeasured':   'ⓘ How is this measured?',
    'blillu.setup':         'Setup',
    'blillu.checklist':     'Before you start',
    'blillu.close':         'Got it',

    // ── Module 3: Ranking / Dragons Score (director, desktop-first) ──────────
    'rank.title':           'Dragons Score',
    'rank.subtitle':        'Who is performing best - fairly compared',
    'rank.cohort':          'Compare within',
    'rank.byAge':           'By age group',
    'rank.byYear':          'By birth year',
    'rank.byCat':           'By category',
    'rank.bornIn':          'Born in',
    'rank.allYears':        'All years',
    'hc.hint':              'Runs automatically. Green = fine, amber = look when convenient, red = act now.',
    'hc.ok':                'OK',
    'hc.warn':              'Check',
    'hc.fail':              'PROBLEM',
    'hc.run':               'running...',
    'hc.rerun':             'Run again',
    'hc.checkError':        'Check could not run - try again.',
    'hc.anon':              'Strangers locked out',
    'hc.anonOk':            'All {count} sensitive tables refuse anonymous access.',
    'hc.anonFail':          'OPEN TO ANONYMOUS ACCESS: {tables}. Contact support immediately.',
    'hc.viewSpeed':         'Reporting speed',
    'hc.viewSpeedDetail':   'Season summary loaded in {ms} ms.',
    'hc.viewSpeedError':    'The season summary failed to load - the timeout bug pattern. Investigate.',
    'hc.blankEvals':        'Evaluations without an author',
    'hc.blankEvalsBad':     'Evaluations found with a blank identity - they miss the director weight',
    'hc.importedPublished': 'Imports hidden from players',
    'hc.importedPublishedBad': 'Imported evaluations are visible to players - unpublish them',
    'hc.snapshots':         'Score history freshness',
    'hc.snapshotsNone':     'No snapshots yet this season - open the Dragons Score screen to start the history.',
    'hc.snapshotsDetail':   'Last snapshot {days} day(s) ago.',
    'hc.anchors':           'Team anchors',
    'hc.anchorsDetail':     '{anchored} anchors set · {teams} teams have players this season.',
    'hc.fixtures':          'Test data cleaned up',
    'hc.fixturesBad':       'ZZ walkthrough fixtures are still in the database',
    'hc.noDob':             'Birth dates complete',
    'hc.noDobDetail':       '{count} active player(s) without a birth date (they miss birth-year comparisons).',
    'hc.zeroOk':            'None found - as it should be.',
    'hc.roleProbes':        'Deep role probes',
    'hc.roleProbesDetail':  'Player/coach impersonation checks run in maintenance sessions, not here. Last full audit: 2026-07-13.',
    'rank.anchors':         'Anchors',
    'rank.anchorsTitle':    'Team anchors (shelf heights)',
    'rank.anchorsHint':     'Tap a level per team - that is the team\'s shelf height. The number next to it is the exact value if you want to fine-tune (e.g. two Strong teams that are not quite equal). Once every team in this cohort has a value, the Coach column switches to shelf mode (one-shelf rule). Clear the number to remove an anchor.',
    'rank.rung.top':        'Top',
    'rank.rung.strong':     'Strong',
    'rank.rung.dev':        'Development',
    'rank.rung.strug':      'Struggling',
    'rank.anchorsSave':     'Save anchors',
    'rank.anchorsSaved':    'Anchors saved',
    'rank.suggested':       'Suggest: {rung} ({value})',
    'rank.suggestWhy':      'From {n} player(s) in view - selection + test signals average {avg}',
    'rank.suggestNote':     'Suggestions come from selection marks + objective tests (comparable across teams), never from coach scores. They are a starting point - your judgement sets the shelf.',
    'rank.allLine':         'All {line}',
    'rank.player':          'Player',
    'rank.score':           'Score',
    'rank.director':        'Director',
    'rank.coach':           'Coach',
    'rank.selection':       'Selection',
    'rank.measured':        'Measured',
    'rank.n':               'n',
    'rank.confidence':      'Confidence',
    'rank.conf.high':       'High',
    'rank.conf.med':        'Medium',
    'rank.conf.low':        'Low',
    'rank.empty':           'No players in this cohort yet. As evaluations and baseline results come in, scores appear here.',
    // What the score is NOT built from. Shown rather than hidden, because the
    // engine fills a missing axis with 'unproven' (40) and pulls every score
    // toward it, so the result looks completely normal. Director's call, 14 Oct
    // 2026: show the score, and say plainly what is not in it.
    // English only, like the other 48 rank.* keys: this screen is director-only.
    'rank.missingTitle':        'What this score does not include',
    'rank.missingTitleFailed':  'Some of this score could not be loaded',
    'rank.missingMeasured':     'No fitness testing recorded this season, so the measured part is not in these scores.',
    'rank.missingMeasuredFail': 'Fitness results could not be loaded, so the measured part is missing from these scores.',
    'rank.missingAnchors':      'No team anchors set, so the coach part falls back to a simple cohort comparison.',
    'rank.missingAnchorsFail':  'Team anchors could not be loaded, so the coach part falls back to a simple cohort comparison.',
    'rank.missingEvalsFail':    'Evaluations could not be loaded, so these scores are not reliable.',
    'rank.snapshotBlocked':     'Not saved to history: some of the data could not be loaded, and a score built from a gap should not become a permanent record.',
    'rank.legend':          'Score rewards being strong across all three axes; a clear weakness pulls it down. Each axis is a 0 to 100 rank within the cohort (coach marks bias-corrected). ★ = you have evaluated them. Confidence reflects how much data backs the score - a fair read of current standing, not a prediction.',
    'rank.snapshot':        '📌 Save snapshot',
    'rank.snapshotDone':    'Saved {count} scores to the history log for later analysis.',
    'rank.autoSnapshot':    'History auto-updated ({cohorts} age groups snapshotted this week).',
    // ── Bias lens (coaches vs director divergence, #91) ──────────────────────
    'rank.lens':            'Bias lens',
    'rank.lensOff':         'Back to scores',
    'rank.coaches':         'Coaches',
    'rank.you':             'You',
    'rank.tests':           'Tests',
    'rank.dragons':         'Dragons',
    'rank.gap':             'Gap',
    'rank.coachHigh':       'coach high',
    'rank.dirHigh':         'you high',
    'rank.lensHint':        'Where the coaches and you disagree. Each column is a 0-100 rank within this cohort: Coaches = what the team coaches say (bias-corrected), You = your own cross-team evaluations, Tests = objective measures. Gap = coaches minus you; a large gap is worth a second look.',
    'rank.lensFlagged':     '{n} flagged.',

    // ── Director team review (bulk director eval, tap steppers) ──────────────
    'tr.title':             'Team review',
    'tr.pickTeam':          'Pick a team. Players start from their latest coach evaluation (or a tier baseline if none).',
    'tr.gridHint':          'Tap +/- to nudge a player. The overall moves the whole profile and keeps their shape. Expand to set one pillar. Only players you touch are saved.',
    'tr.wholeTeam':         'Whole team',
    'tr.level':             'level',
    'tr.pillars':           'Pillars',
    'tr.hide':              'Hide',
    'tr.otherTeam':         'Pick another team',
    'tr.save':              'Save team',
    'tr.saved':             'Saved {count} players as director evaluations.',
    'tr.noneTouched':       'Nudge at least one player first.',

    // ── Post-game nominations (coach) ────────────────────────────────────────
    'gp.title':             'After the game',
    'gp.date':              'Game date',
    'gp.hint':              'Pick your top 3 (in order) and, if you like, the 2 who had the toughest day. Only you see these.',
    'gp.hintTopOnly':       'Pick up to 3 players who stood out (in order). Only you see these.',
    'gp.top':               'Top',
    'gp.bottom':            'Bottom',
    'gp.bottomShort':       'Btm',
    'gp.noRoster':          'No players on this team yet.',
    'gp.save':              'Save picks',
    'gp.saved':             'Picks saved',
    'gp.needTop':           'Pick at least one top performer.',

    // ── Long-term player tracking (Module 4, director) ───────────────────────
    'track.title':          'Player tracking',
    'track.subtitle':       'How a player is developing, season on season',
    'track.search':         'Search for a player',
    'track.searchPlaceholder': 'Type a name...',
    'track.pickPrompt':     'Search a player above to see their season-by-season story.',
    'track.noMatch':        'No players match that name.',
    'track.oneSeason':      'Only one season of data so far - trends appear once a second season is recorded.',
    'track.teamPath':       'Team path',
    'track.coaches':        'Coaches',
    'track.signals':        'What stands out',
    'track.noSignals':      'No divergence signals - the different views of this player broadly agree.',
    'track.trajectories':   'Trajectories',
    'track.overall':        'Overall (weighted)',
    'track.selection':      'Selection net',
    'track.fitness':        'Fitness (Bronco %ile)',
    'track.score':          'Dragons Score',
    'track.pillars':        'Pillars',
    'track.noms':           'After-game picks',
    'track.nomsBody':       'Top-3 in {top} games ({top1} as #1, {points} pts) - bottom-2 in {bottom}. Across {games} games logged.',
    'track.nomsNone':       'No after-game picks logged for this player yet.',
    'track.trendUp':        'rising',
    'track.trendDown':      'falling',
    'track.trendSteady':    'steady',
    'track.trendSingle':    'one season',
    'track.na':             'No data',
    'track.season':         'Season',

    // ── Director private player notes & flags (Module 4 add-on) ──────────────
    'dn.title':             'Your notes (private)',
    'dn.private':           'Private - only you see this. Never shown to players, parents, or coaches.',
    'dn.status':            'Status',
    'dn.themes':            'Themes',
    'dn.potential':         'Potential',
    'dn.topPerformer':      '★ Top performer today',
    'dn.topSaved':          'Marked top performer',
    'dn.topTally':          'Top performer {count}x (last {date})',
    'dn.topNone':           'Not yet marked a top performer',
    'dn.log':               'Observation log',
    'dn.notePh':            'A quick note about this player...',
    'dn.add':               'Add note',
    'dn.noteSaved':         'Note added',
    'dn.needNote':          'Type a note first.',
    'dn.empty':             'No notes yet.',
    'dn.delete':            'Delete',
    'dn.by_director':       'you',
    'dn.by_codirector':     'co-director',
    'dn.delTitle':          'Delete this note?',
    'dn.delBody':           'This permanently removes the note.',

    'bl.codeActive':        'active',
    'bl.codeRevoked':       'revoked',
    'bl.copyCode':          'Copy',
    'bl.copied':            'Copied',
    'bl.revoke':            'Revoke',
    'bl.addTester':         '+ Add tester code',
    'bl.close':             'Close session',
    'bl.reopen':            'Reopen session',
    'bl.publish':           'Publish results to players',
    'bl.unpublish':         'Unpublish',
    'bl.publishedHint':     'Published - players in this session can see their results.',
    'bl.delete':            'Delete session',
    'bl.deleteTitle':       'Delete "{name}"?',
    'bl.deleteBody':        'All results, players, exercises and tester codes for this session are removed. This cannot be undone.',
    'bl.testerNamePrompt':  'Tester name',

    // ── Module 2: tester (placeholder until the testing screens land) ────────
    'bl.tester.title':      'Baseline testing',
    'bl.tester.welcome':    'Hi {name}',
    'bl.tester.soon':       'The testing screens (stopwatch, rep counter, gate scorer) arrive in the next update. Your session "{session}" is loaded and ready.',
    'bl.tester.signOut':    'Sign out',
    'bl.tester.backToHub':  '← Coach Hub',
    'bl.tester.loadFailed': 'Could not load the session. Check your connection and try again.',
    'bl.tester.pickExercise':'Pick an exercise',
    'bl.tester.toTest':     'To test ({count})',
    'bl.tester.allDone':    'Everyone has been tested for this exercise.',
    'bl.tester.savedAttempts':'Saved this session',
    'bl.tester.skip':       'Skip',
    'bl.tester.retest':     'Test again',
    'bl.tester.resetTitle': 'Remove this time?',
    'bl.tester.resetBody':  'This deletes the saved time so it can be tested again. Only do this if it was recorded by mistake.',
    'bl.tester.resetConfirm': 'Remove time',
    'bl.tester.lap':        'Lap (set)',
    'bl.tester.sets':       'Sets',
    'bl.tester.start':      'START',
    'bl.tester.stop':       'STOP',
    'bl.tester.save':       'Save attempt',
    'bl.tester.discard':    'Discard',
    'bl.tester.saved':      'Saved',
    'bl.tester.savedOffline':'Saved on this device - will sync when back online',
    'bl.tester.pending':    '{count} result(s) waiting to sync',
    'bl.tester.tapRep':     'TAP EACH REP',
    'bl.tester.reps':       'reps',
    'bl.tester.undo':       '↶ Undo last',
    'bl.tester.push':       'PUSH',
    'bl.tester.shot':       'SHOT',
    'bl.tester.ball':       'Ball',

    // ── How a test is recorded (director 2026-10-30) ─────────────────────────
    // Three modes, chosen per test. 'anon' is the cheap one that still gives a
    // real distribution, and the one that stores no personal data at all.
    'bl.mode.player':       'Per player',
    'bl.mode.anon':         'Times only',
    'bl.mode.average':      'Team average',
    'bl.mode.anonCount':    '{count} time(s)',
    'bl.mode.anonNth':      'Time {n}',
    'bl.mode.anonAdd':      '+ RECORD A TIME',
    'bl.mode.anonHint':     'Individual times for the team, with no names. Record one time per player so the team average means the same as a per-player one.',
    'bl.mode.avgDone':      'Entered',
    'bl.mode.avgTodo':      'Not yet',
    'bl.mode.avgHint':      'Enter the team average you measured, and how many players it covers.',
    'bl.mode.avgSave':      'Save team average',
    'bl.mode.avgValue':     'Team average',
    'bl.mode.avgValueSecs': 'Team average (seconds)',
    'bl.mode.avgCount':     'How many players?',
    'bl.mode.avgCountHint': 'An average over 3 players must not count as much as one over 18, so the age-group figure is weighted by this.',
    'bl.mode.avgOver':      'over {count} player(s)',
    'bl.mode.avgInvalid':   'Fill in both the average and how many players it covers.',
    'bl.mode.statMean':     'average',
    'bl.mode.statBest':     'best',
    'bl.mode.statTimes':    'times',
    'bl.mode.noTeam':       'This test records a team figure, but the session is not set to one team, so there is no team to attach it to. Open a session for a single team to use this mode.',
    'bl.avg.open':          'Team averages',
    'bl.avg.title':         'Team averages',
    'bl.avg.hint':          'How each team and age group is doing at each test, from every way of recording it. Nothing here is tied to a child, so it is the view that can be compared with another club.',
    'bl.avg.empty':         'No test results for this season yet.',
    'bl.avg.complete':      'All {teams} team(s) have done this test · {players} player(s)',
    'bl.avg.partial':       '{done} of {teams} teams have done this test · {players} player(s)',
    'bl.avg.teamMeta':      '{count} player(s) · {basis}',
    'bl.avg.best':          'best {value}',
    'bl.teamScope':         'One team, or the whole club?',
    'bl.teamScopeAll':      'Whole club',
    'bl.teamScopeHint':     'Pick one team to allow its coaches to run the session themselves, and to unlock the team-level recording modes. A whole-club session is per player, as before.',
    'bl.modeHint':          'Tap a test, then choose how it is recorded. Only "Per player" reaches a player\u2019s Dragons Score.',
    'bl.modeHintNoTeam':    'A whole-club session records every test per player. Set the session to one team on the first step to unlock team averages.',

    // ── A coach opening a test for their own team (director 2026-10-30) ──────
    'tt.title':             'Test my team',
    'tt.team':              'Which team?',
    'tt.name':              'What is it called?',
    'tt.namePh':            'e.g. November fitness check',
    'tt.date':              'Date',
    'tt.tests':             'Which tests?',
    'tt.testsHint':         'Tap a test to add it, then choose how you will record it. "Times only" needs no names and is the quickest.',
    'tt.create':            'Create and start',
    'tt.created':           'Test created',
    'tt.needTest':          'Pick at least one test.',
    'tt.noTeams':           'You are not assigned to a team, so there is no team to test.',
    'tt.defaultName':       '{team} test',

    // ── Coordinator bands ────────────────────────────────────────────────────
    // Director-facing, so English only. The hints carry the arithmetic that
    // decides what the grading panel's four buttons are worth, which is the
    // one thing a coordinator has to understand before tapping.
    'bands.noTeams':      'No teams for this season yet.',

    // ── Band step (team evaluation: the floor and ceiling per criterion) ─────
    // Said once, at the top of a pass, so these can be sentences rather than
    // labels. Every one of them is about a SQUAD, never about a child.
    'bandstep.intro':       'For each one, tap where your weakest player is, then your strongest.',
    'bandstep.hintLow':     'Tap your weakest player\'s level.',
    'bandstep.hintHigh':    'Now your strongest.',
    'bandstep.weakest':     'weakest',
    'bandstep.strongest':   'strongest',
    'bandstep.allAlike':    'all alike',
    'bandstep.cap':         'Director\'s max: {n}',
    'bandstep.progress':    '{done} of {total} set',
    'bandstep.pillarRange': 'This squad on {pillar}: {low} to {high}',
    'bandstep.pillarHelp':  'The pillar score a player can get, from the averages you just set.',
    'bandstep.unreachable': 'Your floor says {claimed}, but the lowest tap scores {actual}: the taps sit inside the band, not on its ends.',
    'bandstep.continue':    'Continue to players',
    'bandstep.setAll':      'Set {n} more',
    'bandstep.redo':        'Start again',

    // ── Rubric editor (the club's own wording for a level) ───────────────────
    'rubric.title':       'Rubric wording',
    'rubric.subtitle':    'Your club\'s words for each level',
    'rubric.intro':       'Pick a criterion. Changing a level\'s wording changes what that level means from today on. Evaluations already made keep the words they were made with.',
    'rubric.pickAge':     'Age group',
    'rubric.standard':    'Standard wording',
    'rubric.yours':       'Your wording',
    'rubric.changed':     'Changed',
    'rubric.edit':        'Change these words',
    'rubric.save':        'Save wording',
    'rubric.saving':      'Saving...',
    'rubric.saved':       'Saved. New evaluations use these words from now on.',
    'rubric.saveFailed':  'Could not save. Nothing changed.',
    'rubric.revert':      'Back to standard',
    'rubric.reverted':    'Back to the standard wording.',
    'rubric.cancel':      'Cancel',
    'rubric.history':     'Changed {n} time(s). Older evaluations keep their original words.',
    'rubric.blank':       'A level needs words. Use "Back to standard" to remove your version.',
    'rubric.warn':        'This rewrites what a {level} means for every evaluation made from now on. It does not change any score, and it does not change evaluations already made.',

    // ── Coordinator grading panel ────────────────────────────────────────────
    // Short on purpose. This screen is tapped fourteen times in a row and
    // nobody reads the same sentence fourteen times.
    'grade.title':        'Fast grading',
    'grade.subtitle':     'One pillar, one tap per player',
    'grade.pickPillar':   'Pick a pillar.',
    'grade.progress':     '{done} of {total} done',
    'grade.keepers':      '{n} goalkeeper(s) left out. Their own coach evaluates them.',
    'grade.counter':      '{at} of {of}',
    'grade.was':          'so far {n}',
    'grade.top':          'Top',
    'grade.upper':        'Upper',
    'grade.lower':        'Lower',
    'grade.middle':       'Middle',
    'grade.bottom':       'Bottom',
    'grade.only':         'Only mark',
    'grade.skip':         'Skip',
    'grade.skipped':      'Skipped',
    'grade.back':         'Back',
    'grade.lastWorkedOn': 'Last worked on',
    'grade.otherTeam':    '← Another team',
    'grade.otherPillar':  '← Another pillar',
    // The season switcher's title. It used to be written as
    // t('season.pick') || 'Which season?', but t() returns the KEY when a
    // string is missing, and a key is truthy, so the fallback never ran and
    // the modal was titled "season.pick".
    'season.pick':        'Which season?',
    'grade.reviewHint':   'Tap a name to change it.',
    'grade.save':         'Save {n} players',
    'grade.saving':       'Saving...',
    'grade.saved':        'Saved {n} players',
    'grade.saveFailed':   'Could not save. Nothing changed.',
    'grade.nothingToSave':'Nothing to save',
    'grade.coordinator':  'Coordinator',

    // ── The drift check ──────────────────────────────────────────────────────
    // Asked blind, so nothing here may mention what was placed.
    'drift.title':        'Check',
    'drift.ask':          'Who is stronger at {pillar}?',
    'drift.equal':        'About the same',
    'drift.skip':         'Skip check',
    'drift.clean':        '{n} checks, no contradictions.',
    'drift.easierLater':  'You were easier on the later players.',
    'drift.harderLater':  'You were harder on the later players.',
    'drift.unseparated':  '{a} and {b} are both {pos}. You said {a} is stronger.',
    'drift.inverted':     'You placed {w} above {s}, then said {s} is stronger.',
  },

  // Dutch lands here when the director wants it - per-key overrides only,
  // anything missing falls back to English automatically.
  // Dutch (Flemish) overlay - the club-facing surfaces (parents, players,
  // coaches). Director-admin keys stay English and fall back automatically.
  // Informal "je". Placeholders ({name}, {date}, ...) are preserved verbatim.
  nl: {
    'parent.childName':      'Volledige naam van je kind',
    'parent.childDob':       'Geboortedatum van je kind',
    // The tester screens, used on a phone at the side of a pitch, plus the
    // diagram framings.
    'dfx.view.full':           'Heel veld',
    'dfx.view.mid':            'Middenveld',
    'dfx.view.d':              'Cirkel (D)',
    'dfx.view.quarter':        'Hoek',
    'dfx.view.grid':           'Trainingsvak',
    'dfx.view.square':         'Klein vak',
    'bl.results.sets':         'Reeksen',
    'bl.results.fade':         '{pct}% terugval',
    'bl.results.legend':       'Beste resultaat per speler, snelste of hoogste eerst. De stippen vergelijken met het gemiddelde van de ploeg (T) en de leeftijdsgroep (A) - goud is beter, rood is onder.',
    'bl.results.none':         'Nog geen resultaten geregistreerd.',
    'bl.tester.title':         'Baseline testing',
    'bl.tester.welcome':       'Hallo {name}',
    'bl.tester.soon':          'De testschermen (chronometer, herhalingenteller, poortjesscore) komen in de volgende update. Je sessie "{session}" staat klaar.',
    'bl.tester.signOut':       'Afmelden',
    'bl.tester.backToHub':     '← Coachhub',
    'bl.tester.loadFailed':    'De sessie kon niet geladen worden. Controleer je verbinding en probeer opnieuw.',
    'bl.tester.pickExercise':  'Kies een oefening',
    'bl.tester.toTest':        'Te testen ({count})',
    'bl.tester.allDone':       'Iedereen is getest voor deze oefening.',
    'bl.tester.savedAttempts': 'Deze sessie opgeslagen',
    'bl.tester.skip':          'Overslaan',
    'bl.tester.retest':        'Opnieuw testen',
    'bl.tester.resetTitle':    'Deze tijd verwijderen?',
    'bl.tester.resetBody':     'Dit wist de opgeslagen tijd zodat er opnieuw getest kan worden. Doe dit enkel als ze per ongeluk geregistreerd werd.',
    'bl.tester.resetConfirm':  'Tijd verwijderen',
    'bl.tester.lap':           'Ronde (reeks)',
    'bl.tester.sets':          'Reeksen',
    'bl.tester.start':         'START',
    'bl.tester.stop':          'STOP',
    'bl.tester.save':          'Poging opslaan',
    'bl.tester.discard':       'Verwerpen',
    'bl.tester.saved':         'Opgeslagen',
    'bl.tester.savedOffline':  'Opgeslagen op dit toestel - wordt gesynchroniseerd zodra je weer online bent',
    'bl.tester.pending':       '{count} resulta(a)t(en) wachten op synchronisatie',
    'bl.mode.player':          'Per speelster',
    'bl.mode.anon':            'Alleen tijden',
    'bl.mode.average':         'Teamgemiddelde',
    'bl.mode.anonCount':       '{count} tijd(en)',
    'bl.mode.anonNth':         'Tijd {n}',
    'bl.mode.anonAdd':         '+ TIJD OPNEMEN',
    'bl.mode.anonHint':        'Individuele tijden voor het team, zonder namen. Neem een tijd per speelster op, dan betekent het teamgemiddelde hetzelfde als een gemiddelde per speelster.',
    'bl.mode.avgDone':         'Ingevuld',
    'bl.mode.avgTodo':         'Nog niet',
    'bl.mode.avgHint':         'Vul het teamgemiddelde in dat je gemeten hebt, en over hoeveel speelsters het gaat.',
    'bl.mode.avgSave':         'Teamgemiddelde opslaan',
    'bl.mode.avgValue':        'Teamgemiddelde',
    'bl.mode.avgValueSecs':    'Teamgemiddelde (seconden)',
    'bl.mode.avgCount':        'Hoeveel speelsters?',
    'bl.mode.avgCountHint':    'Een gemiddelde over 3 speelsters mag niet even zwaar wegen als een over 18, dus het leeftijdsgemiddelde weegt hiermee.',
    'bl.mode.avgOver':         'over {count} speelster(s)',
    'bl.mode.avgInvalid':      'Vul zowel het gemiddelde als het aantal speelsters in.',
    'bl.mode.statMean':        'gemiddeld',
    'bl.mode.statBest':        'beste',
    'bl.mode.statTimes':       'tijden',
    'bl.mode.noTeam':          'Deze test neemt een teamcijfer op, maar de sessie staat niet op een team, dus er is geen team om het aan te hangen. Open een sessie voor een enkel team om deze manier te gebruiken.',
    'tt.title':                'Test mijn team',
    'tt.team':                 'Welk team?',
    'tt.name':                 'Hoe heet het?',
    'tt.namePh':               'bv. fitheidstest november',
    'tt.date':                 'Datum',
    'tt.tests':                'Welke tests?',
    'tt.testsHint':            'Tik een test aan om hem toe te voegen, en kies dan hoe je hem opneemt. "Alleen tijden" heeft geen namen nodig en gaat het snelst.',
    'tt.create':               'Aanmaken en starten',
    'tt.created':              'Test aangemaakt',
    'tt.needTest':             'Kies minstens een test.',
    'tt.noTeams':              'Je bent niet aan een team toegewezen, dus er is geen team om te testen.',
    'tt.defaultName':          'test {team}',
    'bl.tester.tapRep':        'TIK ELKE HERHALING',
    'bl.tester.reps':          'herhalingen',
    'bl.tester.undo':          '↶ Laatste ongedaan maken',
    'bl.tester.push':          'PUSH',
    'bl.tester.shot':          'SHOT',
    'bl.tester.ball':          'Bal',
    // Baseline testing: run pitch-side by coaches and testers on a phone.
    'bl.title':                'Baseline testing',
    'bl.subtitle':             'Veldtestsessies',
    'bl.new':                  '+ Nieuwe sessie',
    'bl.empty':                'Nog geen baselinesessies. Maak er een om veldtesten in te plannen en testercodes uit te delen.',
    'bl.statusOpen':           'open',
    'bl.statusClosed':         'gesloten',
    'bl.published':            'gepubliceerd',
    'bl.wizTitle':             'Nieuwe baselinesessie',
    'bl.step':                 'Stap {n} van 4',
    'bl.stepBasics':           'Basis',
    'bl.stepExercises':        'Oefeningen',
    'bl.stepPlayers':          'Spelers',
    'bl.stepTesters':          'Testercodes',
    'bl.basicsName':           'Naam van de sessie',
    'bl.basicsNamePh':         'bv. U14 baseline - september',
    'bl.basicsDate':           'Datum',
    'bl.selfTestLabel':        'Spelers testen zichzelf',
    'bl.selfTestOn':           'Aan - spelers mogen hun eigen tijd registreren',
    'bl.selfTestOff':          'Uit - enkel officiele testers',
    'bl.selfTestHint':         'Als dit aanstaat, ziet elke speler in deze sessie ze bij "Test jezelf" zolang de sessie open is. Zelf geregistreerde tijden blijven apart van de officiele resultaten.',
    'bl.exercisesHint':        'Kies de oefeningen die deze sessie afwerkt.',
    'bl.playersHint':          'Voeg hele ploegen of losse spelers toe.',
    'bl.playersAddTeam':       'Een ploeg toevoegen',
    'bl.playersAddIndividual': 'Losse spelers toevoegen',
    'bl.playersSearch':        'Zoek een speler op naam...',
    'bl.playersChosen':        '{count} speler(s) geselecteerd',
    'bl.newTest':              '+ Nieuwe test',
    'bl.newTestTitle':         'Een test aanmaken',
    'bl.newTestName':          'Naam van de test',
    'bl.newTestNamePh':        'bv. tijdrit 1 km',
    'bl.newTestType':          'Hoe wordt het gemeten?',
    'bl.newTestTypeStopwatch': 'Chronometer (een tijd)',
    'bl.newTestTypeReps':      'Aantal (herhalingen / punten)',
    'bl.newTestTypeDistance':  'Afstand / getal',
    'bl.newTestBetter':        'Wat is beter?',
    'bl.newTestBetterMin':     'Lager is beter (bv. een tijd)',
    'bl.newTestBetterMax':     'Hoger is beter (bv. herhalingen)',
    'bl.newTestTags':          'Labels (optioneel, gescheiden door komma)',
    'bl.newTestTagsPh':        'bv. uithouding, snelheid',
    'bl.newTestCreate':        'Test aanmaken',
    'bl.newTestNeedName':      'Geef de test een naam.',
    'bl.newTestExists':        'Die test bestaat al - ze is geselecteerd.',
    'bl.newTestCreated':       'Test aangemaakt en geselecteerd.',
    'bl.playersExternals':     'Gasten toegevoegd',
    'bl.externalTag':          'gast',
    'bl.externalNoMatch':      'Geen spelers gevonden. Je kan deze naam als gast toevoegen.',
    'bl.externalAdd':          '"{name}" als gast toevoegen',
    'bl.externalNeedName':     'Typ eerst een naam.',
    'bl.testersHint':          'Voeg een code per tester toe (hun naam zit erin). Deel de codes persoonlijk - ze loggen er langs het veld mee in.',
    'bl.testerName':           'Naam van de tester',
    'bl.testerAdd':            '+ Tester toevoegen',
    'bl.testerAddTitle':       'Een tester toevoegen',
    'bl.testerFromStaff':      'Een coach of trainer toewijzen',
    'bl.testerOrName':         'Of voeg een eenmalige naam toe',
    'bl.next':                 'Volgende →',
    'bl.back':                 '← Terug',
    'bl.create':               'Sessie aanmaken',
    'bl.createdToast':         'Sessie aangemaakt',
    'bl.needName':             'Geef de sessie een naam.',
    'bl.needExercise':         'Kies minstens een oefening.',
    'bl.needPlayer':           'Voeg minstens een speler toe.',
    'bl.needTester':           'Voeg minstens een testercode toe.',
    'bl.detailExercises':      'Oefeningen',
    'bl.detailPlayers':        'Spelers',
    'bl.detailTesters':        'Testercodes',
    'bl.detailProgress':       'Voortgang',
    'bl.progressLine':         '{done} van {total} getest',
    'bl.viewResults':          '📊 Resultaten bekijken',
    'blpdf.download':          '⬇️ PDF downloaden',
    'blpdf.title':             'Baseline-PDF',
    'blpdf.layout':            'Opmaak',
    'blpdf.summary':           'Overzicht met de beste resultaten',
    'blpdf.history':           'Volledige historiek (alle pogingen)',
    'blpdf.dots':              'Toon vergelijkingsstippen ploeg (T) en leeftijdsgroep (A)',
    'blpdf.generate':          'PDF maken',
    'blpdf.legend':            'De stippen vergelijken met het gemiddelde van de ploeg (T) en de leeftijdsgroep (A): goud = beter, rood = onder, marine = rond het gemiddelde.',
    'blpdf.attempts':          'Pogingen',
    'blpdf.external':          'Extern / geen ploeg',
    'blillu.howMeasured':      'ⓘ Hoe wordt dit gemeten?',
    'blillu.setup':            'Opstelling',
    'blillu.checklist':        'Voor je begint',
    'blillu.close':            'Begrepen',
    'bl.codeActive':           'actief',
    'bl.codeRevoked':          'ingetrokken',
    'bl.copyCode':             'Kopieren',
    'bl.copied':               'Gekopieerd',
    'bl.revoke':               'Intrekken',
    'bl.addTester':            '+ Testercode toevoegen',
    'bl.close':                'Sessie sluiten',
    'bl.reopen':               'Sessie heropenen',
    'bl.publish':              'Resultaten publiceren voor spelers',
    'bl.unpublish':            'Depubliceren',
    'bl.publishedHint':        'Gepubliceerd - spelers in deze sessie zien hun resultaten.',
    'bl.delete':               'Sessie verwijderen',
    'bl.deleteTitle':          '"{name}" verwijderen?',
    'bl.deleteBody':           'Alle resultaten, spelers, oefeningen en testercodes van deze sessie verdwijnen. Dit kan niet ongedaan gemaakt worden.',
    'bl.testerNamePrompt':     'Naam van de tester',
    // DTA library: the screen coaches browse for club material and, since the
    // season plan import, the live practices too.
    'dta.title':           'DTA-bibliotheek',
    'dta.subtitle':        'Clubmateriaal voor coaches',
    'dta.add':             '+ Materiaal toevoegen',
    'dta.typePdf':         'PDF uploaden',
    'dta.typeArticle':     'Artikel (in de app geschreven)',
    'dta.typeLink':        'Link (bv. oefenvideo)',
    'dta.catWarmup':       'Opwarming',
    'dta.catTechnical':    'Technisch',
    'dta.catTactical':     'Tactisch',
    'dta.catPhysical':     'Fysiek',
    'dta.catMental':       'Mentaal',
    'dta.catSocial':       'Sociaal',
    'dta.catClub':         'Clubinfo',
    'dta.allCats':         'Alle',
    'dta.allAges':         'Alle leeftijden',
    'dta.fieldTitle':      'Titel',
    'dta.fieldBody':       'Artikeltekst',
    'dta.bodyPlaceholder': 'Schrijf de sessie hier. Gebruik de knoppen hierboven voor titels, vet, lijsten en citaten.',
    'dta.previewLabel':    'Voorbeeld (wat coaches zien)',
    'dta.previewEmpty':    'Je opgemaakte artikel verschijnt hier terwijl je typt.',
    'dta.mdHeading':       'Titel',
    'dta.mdBold':          'Vet',
    'dta.mdItalic':        'Cursief',
    'dta.mdBullet':        'Opsomming',
    'dta.mdNumber':        'Genummerde lijst',
    'dta.mdQuote':         'Citaat',
    'dta.figures':         'Figuren (schema\'s en afbeeldingen)',
    'dta.addDiagram':      '+ Schema',
    'dta.addField':        '+ Veld tekenen',
    'dta.addImage':        '+ Afbeelding',
    'dta.figCaption':      'Onderschrift (optioneel)',
    'dta.figInsert':       'In tekst invoegen',
    'dta.figEdit':         'Aanpassen',
    'dta.figChange':       'Wijzigen',
    'dta.figReplace':      'Vervangen',
    'dta.figRemove':       'Verwijderen',
    'dta.figUnplaced':     'nog niet geplaatst',
    'dta.figEmpty':        'Nog geen figuren. Voeg een schema toe, teken een veld of upload een afbeelding en plaats het daarna waar je het in de tekst wil.',
    'dta.figDiagram':      'Schema',
    'dta.figField':        'Veldschema',
    'dta.figImage':        'Afbeelding',
    'dta.pickDiagram':     'Kies een schema',
    'dta.fieldUrl':        'Link (https://...)',
    'dta.fieldFile':       'PDF-bestand (max 10 MB)',
    'dta.fieldAges':       'Leeftijdsgroepen (leeg laten = iedereen)',
    'dta.bulkAdd':         'Meerdere PDFs toevoegen',
    'dta.bulkTitle':       'Meerdere PDFs in een keer toevoegen',
    'dta.bulkHint':        'Kies zoveel PDFs als je wil. Elk bestand wordt een apart item in de bibliotheek, met de bestandsnaam als titel. De categorie, het publiek en de leeftijden hieronder gelden voor allemaal.',
    'dta.bulkUpload':      '{n} uploaden',
    'dta.bulkUploading':   'Bezig met uploaden...',
    'dta.bulkDone':        'Toegevoegd',
    'dta.bulkFailed':      'Mislukt',
    'dta.bulkNeedsTitle':  'Elk bestand heeft een titel nodig.',
    'dta.bulkAllDone':     '{n} toegevoegd aan de bibliotheek',
    'dta.bulkPartly':      '{ok} toegevoegd, {bad} mislukt. De mislukte staan er nog bij, druk op Uploaden om ze opnieuw te proberen.',
    'dta.fieldTitleNl':    'Titel (Nederlands)',
    'dta.fieldBodyNl':     'Artikeltekst (Nederlands)',
    'dta.previewLabelNl':  'Voorbeeld (Nederlands)',
    'dta.nlFallback':      'Leeg laten om de Engelse versie te tonen',
    'dta.fieldAudience':   'Wie dit mag zien',
    'dta.audAll':          'Iedereen',
    'dta.audTrainers':     'Enkel trainers',
    'dta.searchPh':        'Zoek artikels...',
    'dta.fieldCategory':   'Categorie',
    'dta.allCatsLong':     'Alle categorieen',
    'dta.allStatus':       'Alle statussen',
    'dta.allAudiences':    'Iedereen + trainers',
    'dta.allAgesShort':    'Alle leeftijden',
    'dta.filterStatus':    'Publicatiestatus',
    'dta.showing':         '{shown} van {total} getoond',
    'dta.viewLabel':       'Weergave',
    'dta.viewList':        'Lijstweergave',
    'dta.viewGrid':        'Kaartweergave',
    'dta.colTitle':        'Titel',
    'dta.colAges':         'Leeftijden',
    'dta.colStatus':       'Status',
    'dta.colViews':        'Geopend',
    'dta.preview':         'Voorbeeld',
    'dta.planPdf':         'PDF',
    'dta.previewNote':     'Zo ziet een coach het. Er wordt niets gepubliceerd of geregistreerd.',
    'dta.trainerSection':  'Traineroefeningen',
    'dta.dailyPick':       'Artikel van de dag',
    'dta.noResults':       'Niets komt overeen met je zoekopdracht.',
    'dta.illoBadType':     'Kies een PNG-, JPG- of WebP-afbeelding.',
    'dta.pinned':          'Vastgezet',
    'dta.pin':             'Vastzetten',
    'dta.unpin':           'Losmaken',
    'dta.publish':         'Publiceren',
    'dta.unpublish':       'Depubliceren',
    'dta.publishedTag':    'gepubliceerd',
    'dta.draftTag':        'klad',
    'dta.replaceFile':     'Bestand vervangen',
    'dta.edit':            'Aanpassen',
    'dta.delete':          'Verwijderen',
    'dta.deleteTitle':     '"{title}" verwijderen?',
    'dta.deleteBody':      'Coaches verliezen meteen de toegang. Een geuploade PDF wordt ook uit de opslag verwijderd.',
    'dta.save':            'Opslaan',
    'dta.saved':           'Opgeslagen',
    'dta.invalidUrl':      'Dat lijkt geen geldig webadres.',
    'dta.missingTitle':    'Geef het eerst een titel.',
    'dta.missingFile':     'Kies eerst een PDF-bestand.',
    'dta.fileTooBig':      'Dat bestand is groter dan 10 MB. Comprimeer het en probeer opnieuw.',
    'dta.notPdf':          'Enkel PDF-bestanden kunnen geupload worden.',
    'dta.uploading':       'Uploaden...',
    'dta.viewsLine':       'Geopend door {count} coach(es)',
    'dta.viewsNone':       'Nog niet geopend',
    'dta.empty':           'Nog geen materiaal. Voeg eerst de standaard opwarming toe en zet ze vast zodat ze bovenaan blijft.',
    'dta.coachEmpty':      'Nog niets gepubliceerd. Opwarmingen, oefeningen en clubdocumenten verschijnen hier.',
    'dta.openPdf':         'PDF openen',
    'dta.openLink':        'Link openen',
    'dta.read':            'Gelezen',
    'dta.loadFailed':      'De bibliotheek kon niet geladen worden. Probeer het later opnieuw.',
    'dta.pdfFailed':       'De PDF kon niet geopend worden. Controleer je verbinding en probeer opnieuw.',
    // Coach-facing screens: line-ups, the diagram editor and after-the-game.
    // The family-facing screens were already Dutch; these were not.
    'home.noPlayersYet': 'Nog geen spelers in een ploeg. Importeer je ledenlijst en de ploegen verschijnen hier.',
    'home.searchTeam':   'Zoek een ploeg',
    'home.noTeamMatch':  'Geen ploeg gevonden voor "{q}".',
    'home.lastWorkedOn': 'Laatst mee bezig',
    'lu.title':          'Opstellingen',
    'lu.new':            '+ Nieuwe opstelling',
    'lu.newTitle':       'Nieuwe opstelling',
    'lu.matches':        'Wedstrijden',
    'lu.noneYet':        'Nog geen opstellingen. Tik op "Nieuwe opstelling" om te beginnen.',
    'lu.date':           'Wedstrijddatum',
    'lu.opponent':       'Tegenstander (optioneel)',
    'lu.opponentPh':     'bv. Antwerp',
    'lu.formation':      'Opstelling',
    'lu.participation':  'Speeltijd dit seizoen',
    'lu.player':         'Speler',
    'lu.started':        'Basis',
    'lu.sub':            'Wissel',
    'lu.out':            'Afwezig',
    'lu.mainRole':       'Hoofdpositie',
    'lu.editHint':       'Tik op een plek om een speler te zetten. {done} van {total} geplaatst.',
    'lu.subs':           'Wissels',
    'lu.subsHint':       'Tik voor elke wissel op de positie(s) die hij of zij kan spelen. Ze verschijnen in het wisselvak op de gedeelde afbeelding.',
    'lu.benchEmpty':     'Iedereen staat op het veld.',
    'lu.download':       'Afbeelding downloaden',
    'lu.share':          'Delen',
    'lu.pickPlayer':     'Wie speelt hier?',
    'lu.allPlaced':      'Elke speler is al geplaatst.',
    'lu.fieldFull':      'Het veld is vol - haal eerst iemand weg.',
    'lu.save':           'Opstelling opslaan',
    'lu.saved':          'Opstelling opgeslagen',
    'lu.delete':         'Verwijderen',
    'lu.deleteTitle':    'Deze opstelling verwijderen?',
    'lu.deleteBody':     'De opstelling en de bijhorende speeltijd verdwijnen. Dit kan niet ongedaan gemaakt worden.',
    'dfx.title':         'Veldschema',
    'dfx.player':        '+ Speler',
    'dfx.opp':           '+ Tegenstander',
    'dfx.ball':          '+ Bal',
    'dfx.run':           '+ Loopactie',
    'dfx.pass':          '+ Pass',
    'dfx.zone':          '+ Zone',
    'dfx.text':          '+ Label',
    'dfx.hint':          'Tik op een knop om iets op het veld te zetten en sleep het op zijn plaats. Tik op een item om het te selecteren, het label of de kleur aan te passen of het te verwijderen. Sleep het kleine middelste bolletje van een pijl om ze te buigen.',
    'dfx.selectHint':    'Tik op een item op het veld om het aan te passen.',
    'dfx.labelPh':       'A-Z / 0-9',
    'dfx.textPh':        'Tekst van het label',
    'dfx.textDefault':   'Label',
    'dfx.gold':          'Onze speler',
    'dfx.red':           'Uitgelicht',
    'dfx.colour':        'Kleur',
    'dfx.duplicate':     'Dupliceren',
    'dfx.delete':        'Verwijderen',
    'dfx.straighten':    'Recht maken',
    'dfx.clear':         'Alles wissen',
    'dfx.save':          'Schema opslaan',
    'gp.title':          'Na de wedstrijd',
    'gp.date':           'Wedstrijddatum',
    'gp.hint':           'Kies je top 3 (in volgorde) en, als je wil, de 2 die het het lastigst hadden. Alleen jij ziet dit.',
    'gp.hintTopOnly':    'Kies tot 3 spelers die eruit sprongen (in volgorde). Alleen jij ziet dit.',
    'gp.top':            'Top',
    'gp.bottom':         'Laag',
    'gp.bottomShort':    'Laag',
    'gp.noRoster':       'Nog geen spelers in deze ploeg.',
    'gp.save':           'Keuzes opslaan',
    'gp.saved':          'Keuzes opgeslagen',
    'gp.needTop':        'Kies minstens een uitblinker.',
    // Common + gate
    'common.save':    'Opslaan',
    'common.cancel':  'Annuleren',
    'common.close':   'Sluiten',
    'common.back':    'Terug',
    'common.loading': 'Laden...',
    'common.error':   'Er ging iets mis. Probeer het opnieuw.',
    'gate.subtitle':        'Spelersontwikkeling, seizoen na seizoen',
    'gate.codeLabel':       'Teamtoegangscode',
    'gate.codePlaceholder': 'Voer je code in',
    'gate.continue':        'Doorgaan →',

    // Parent landing
    'parent.gateLink':     'Ouder? Vraag toegang aan voor je kind',
    'parent.title':        'Spelertoegang aanvragen',
    'parent.subtitle':     'Voor ouders van spelers bij je club',
    'parent.intro':        'Vul dit een keer per kind in. De club bekijkt elke aanvraag; je code werkt na goedkeuring.',
    'parent.parentName':   'Je naam',
    'parent.parentEmail':  'Je e-mail (enkel gebruikt door de club om de aanvraag te verifieren)',
    'parent.submit':       'Toegang aanvragen',
    'parent.missingFields':'Vul je naam en de naam van je kind in.',
    'parent.submitFailed': 'De aanvraag kon niet verzonden worden. Controleer je verbinding en probeer opnieuw.',
    'parent.confirmTitle': 'Aanvraag ontvangen',
    'parent.confirmCode':  'Je persoonlijke toegangscode',
    'parent.confirmSave':  'Bewaar deze code nu - schrijf ze op of neem een screenshot. Ze wordt maar een keer getoond.',
    'parent.confirmWhen':  'De code werkt zodra de club je aanvraag goedkeurt. De club stuurt geen melding - probeer gewoon na enkele dagen in te loggen.',
    'parent.confirmLink':  'Of bewaar deze persoonlijke link als bladwijzer:',
    'parent.confirmDone':  'Klaar',
    'parent.copyCode':     'Code kopieren',
    'parent.copied':       'Gekopieerd',

    // Coach/trainer self-apply (public screen)
    'staffApply.gateLink':  'Coach of trainer? Vraag toegang aan',
    'staffApply.title':     'Toegang aanvragen',
    'staffApply.subtitle':  'Coaches & trainers',
    'staffApply.intro':     'Vul dit in en de club bekijkt het. Je krijgt een code die werkt zodra de directeur je goedkeurt.',
    'staffApply.name':      'Je volledige naam',
    'staffApply.contact':   'E-mail of telefoon (zodat de club je kan bereiken)',
    'staffApply.teamNote':  'Met welk team of welke teams help je?',
    'staffApply.submit':    'Aanvraag versturen',
    'staffApply.missingName':'Vul je naam in.',
    'staffApply.submitFailed':'Verzenden mislukt. Probeer opnieuw.',
    'staffApply.confirmTitle':'Aanvraag verzonden',
    'staffApply.confirmCode':'Je code (bewaar ze):',
    'staffApply.confirmSave':'Schrijf deze code nu op. Het is je login zodra de directeur je goedkeurt.',
    'staffApply.confirmWhen':'De code werkt pas als de club je aanvraag goedkeurt.',
    'staffApply.confirmDone':'Klaar',
    'staffApply.copyCode':  'Code kopieren',
    'staffApply.copied':    'Gekopieerd',

    // Player home + evaluation
    'player.homeTitle':     'Mijn evaluatie',
    'player.greeting':      'Hallo {name}',
    'player.unread':        'NIEUW',
    'player.read':          'Gezien',
    'player.empty':         'Nog niets gepubliceerd. Je evaluatie verschijnt hier zodra de club ze met je deelt.',
    'player.baselineTitle': 'Basistests',
    'player.baselineSoon':  'Je fitheids- en vaardigheidstests verschijnen hier zodra de club ze deelt.',
    'player.bl.sessionTitle':  'Testresultaten',
    'player.bl.sessionIntro':  'Je beste resultaat voor elke test in deze sessie. Dit zijn je eigen cijfers - het doel is ze de volgende keer te verbeteren.',
    'player.bl.attempts':      '{count} pogingen',
    'player.bl.noResults':     'Geen resultaten voor jou in deze sessie.',
    'player.bl.progressTitle': 'Je vooruitgang in de tijd',
    'player.bl.progressSub':   'Hoe je tests evolueren',
    'player.bl.progressIntro': 'Elke lijn is een test over je sessies heen. De lijn stijgt naarmate je beter wordt - die evolutie in de tijd telt het meest.',
    'player.bl.needTwo':       'Een tweede sessie nodig om te tonen: {names}.',
    'player.bl.trend.improving': '▲ Vooruitgang',
    'player.bl.trend.declining': '▼ Minder dan vorige keer - blijf oefenen',
    'player.bl.trend.steady':    '→ Stabiel',
    'player.bl.trend.single':    'Eerste resultaat opgeslagen',
    'player.summaryTitle':   'Je evaluatie',
    'player.summaryBasedOn': 'Samengevoegd uit meerdere coachbeoordelingen',
    'player.summaryUpdated': 'Bijgewerkt {date}',
    'player.summaryIntro':   'Deze niveaus zijn het gemiddelde van alle beoordelingen die de club met je gedeeld heeft - ze weerspiegelen de kijk van het hele coachteam, niet de mening van een coach.',
    'player.feedbackTitle':  'Feedback van het coachteam',
    'sr.coachTitle':        'Voor jou, van de ploeg',
    'sr.coachCounts':       '{total} geantwoord · {happy} tevreden zo',
    'sr.coachHappy':        '{n} zeiden dat er niets moet veranderen.',
    'sr.coachAnon':         'Anoniem: er is nooit een naam bij bewaard, dus ze zijn zelfs in de database niet te herleiden.',
    'sr.export':            'Exporteren naar spreadsheet',
    'sr.title':             'Antwoorden',
    'sr.subtitle':          'Wat de ploeg na een match doorstuurde',
    'sr.pickTeam':          'Team',
    'sr.empty':             'Nog niets binnengekomen voor dit team.',
    'sr.versus':            'tegen {team}',
    'sr.repliedCount':      '{n} van {total} geantwoord',
    'sr.avgScore':          'gemiddeld {avg}/5',
    'sr.onTheMatch':        'Over de match:',
    'sr.wentWell':          'Ging goed:',
    'sr.workOn':            'Werkpunt:',
    'sr.praised':           'Een ploegmaat zei:',
    'sr.notYet':            'Nog niet binnen:',
    'sr.noReply':           'geen antwoord',
    'sr.noneThisMatch':     'Geen reflecties voor deze match, enkel ploegmaatnotities.',
    'tf.title':             'Teambestanden',
    'tf.subtitle':          'Seizoenskalenders en wedstrijdbriefings',
    'tf.pickTeam':          'Team',
    'tf.addTitle':          'Bestand toevoegen',
    'tf.addHint':           'PDF of PowerPoint, tot 50 MB. Niets bereikt de ploeg tot je het publiceert, dus je kan donderdag uploaden en zaterdag vrijgeven.',
    'tf.kindBriefing':      'Wedstrijdbriefing',
    'tf.kindSchedule':      'Seizoenskalender',
    'tf.titlePlaceholder':  'Hoe moet de ploeg het zien staan?',
    'tf.listTitle':         'Al geüpload',
    'tf.listEmpty':         'Nog niets geüpload voor dit team.',
    'tf.live':              'Ploeg ziet het',
    'tf.draft':             'niet gepubliceerd',
    'tf.publish':           'Publiceren',
    'tf.unpublish':         'Intrekken',
    'tf.delete':            'Verwijderen',
    'tf.deleteTitle':       'Dit bestand verwijderen?',
    'tf.deleteBody':        'De ploeg verliest meteen toegang en het bestand zelf wordt verwijderd. Dit kan niet ongedaan gemaakt worden.',
    'tf.deleteFailed':      'Kon dat niet verwijderen.',
    'tf.uploading':         'Uploaden...',
    'tf.uploaded':          'Geüpload. Publiceer het wanneer je klaar bent.',
    'tf.uploadFailed':      'Upload mislukt. Er is niets toegevoegd.',
    'tf.tooBig':            'Dat bestand is groter dan 50 MB. Comprimeer het of splits het op.',
    'share.title':          'Delen',
    'share.subtitle':       'Wat gezinnen te zien krijgen, en hoeveel',
    'share.statWritten':    'geschreven',
    'share.statPublished':  'gedeeld',
    'share.statWaiting':    'wacht',
    'share.statNominated':  'met keuzes',
    'share.formatTitle':    'Hoeveel ze zien',
    'share.formatHint':     'Stel dit in voor je deelt: het bepaalt wat er echt verstuurd wordt. De clubinstelling geldt overal; kies een leeftijdsgroep om die iets anders te geven. Een stip betekent dat die leeftijd een eigen instelling heeft.',
    'share.scopeClub':      'Hele club',
    'share.modeTopBottom':  'Alleen sterke punten en werkpunten',
    'share.modeTopBottomHint': 'In plaats van alle 25 vaardigheden alleen die de coach als beste en als werkpunt aanduidde. Uit = het volledige beeld, zoals nu.',
    'share.swDescriptors':  'Toon de omschrijving',
    'share.swDescriptorsHint': 'De zin onder elke vaardigheid die uitlegt wat dat niveau inhoudt.',
    'share.swStrongText':   'Toon de sterke punten van de coach',
    'share.swStrongTextHint': 'De vrije tekst van de coach, samengevoegd en nooit op naam.',
    'share.swWorkingText':  'Toon de werkpunten van de coach',
    'share.swWorkingTextHint': 'Idem, voor waaraan gewerkt wordt.',
    'share.clearOverride':  'Wis de instelling voor {scope} en erf weer over',
    'share.overrideCleared': 'Erft weer over',
    'share.nominationGap':  '{n} evaluaties hebben nog geen keuzes. Die spelers zien de volledige lijst tot een coach een beste en een werkpunt aanduidt.',
    'share.queueTitle':     'Wacht om gedeeld te worden',
    'share.allTeams':       'Alle teams',
    'share.queueEmpty':     'Nog geen evaluaties dit seizoen.',
    'share.waitingTag':     '{n} wachten',
    'share.allSharedTag':   'alles gedeeld',
    'share.teamCounts':     '{published} van {total} gedeeld · {nominated} met keuzes',
    'share.andMore':        ' en nog {n}',
    'share.publishTeam':    'Deel {n}',
    'share.unpublishTeam':  'Intrekken',
    'share.confirmPublishTitle':   'Delen met gezinnen?',
    'share.confirmUnpublishTitle': 'Intrekken bij gezinnen?',
    'share.confirmPublish':   'Dit stuurt {n} evaluaties van {team} naar de spelers en ouders met toegang. Ze kunnen ze meteen lezen.',
    'share.confirmUnpublish': 'Dit verbergt {n} al gedeelde evaluaties van {team} weer. Wie er al een las, heeft die gezien.',
    'share.confirmGo':      'Ja, doe maar',
    'share.publishedN':     '{n} gedeeld',
    'share.withdrewN':      '{n} ingetrokken',
    'share.publishFailed':  'Kon niet afronden. Er is verder niets gewijzigd.',
    'share.saveFailed':     'Kon die instelling niet bewaren.',
    'player.strengthsTitle': 'Jouw sterke punten',
    'player.workingTitle':   'Jouw werkpunten',
    'player.strongPoints':   'Sterke punten',
    'player.workingPoints':  'Werkpunten',
    'player.trLabel':        'Vertaling',
    'player.assessedBy':     'Beoordeeld door: {names}',
    'player.acknowledge':    'Markeer als gezien',
    'player.allSeen':        'Je bent helemaal bij',
    'player.ackFailed':      'Kon niet opslaan - probeer opnieuw.',
    'player.signOut':        'Afmelden',
    'player.loadFailed':     'Kon je evaluatie niet laden. Probeer het later opnieuw.',
    'player.downloadReport': '⬇ Download mijn rapport (PDF)',
    'player.sharedTitle':    'Met jou gedeeld',
    'player.sharedCard':     'Evaluatie gedeeld door de club',
    'player.sharedOf':       'Gedeelde evaluatie · {date}',

    // Player intro
    'pintro.title':    'Voor je naar de scores kijkt',
    'pintro.continue': 'Toon mijn evaluatie →',
    'pintro.p1': 'Een evaluatie is een feedbackmoment. Zo vertellen onze coaches je wat ze zagen - wat goed gaat, en waar de volgende stap ligt. Het is het begin van een gesprek, geen eindoordeel.',
    'pintro.p2': 'Jij of je ouders kunnen het met delen ervan eens of oneens zijn, en dat is oke. Wat je hier leest, is hoe het coachteam je spel echt ervaren heeft tijdens de periode die deze evaluatie beslaat - niet een wedstrijd, en niet een mening.',
    'pintro.p3': 'Wat we het belangrijkst vinden, is vooruitgang in de tijd. Hockey is een complexe sport: spelers ontwikkelen met sprongen, verschillende coaches merken verschillende dingen op, en een dip op papier komt vaak net voor een stap vooruit. Een evaluatie is een momentopname; het patroon over de seizoenen heen is wat telt.',
    'pintro.p4': 'Evaluaties zo delen is nieuw bij onze club. Naarmate er meer evaluaties op een plek verzameld worden, seizoen na seizoen, wordt het beeld dat je hier ziet steeds vollediger en nauwkeuriger.',
    'pintro.p5': 'Een ding om te weten: deze scores zijn maar een deel van hoe de sportieve staf naar ontwikkeling kijkt. We wegen ook veel dingen mee die niet in een cijfer te vatten zijn - en die context blijft bij de staf, waar ze het sportieve beleid stuurt en beslissingen over selecties, teamsamenstelling en speelstijl.',
    'pintro.legendTitle': 'Wat de woorden betekenen',
    'pintro.lvl1': 'De basis van deze vaardigheid opbouwen - elke speler begint hier.',
    'pintro.lvl2': 'Steeds vaker juist; de constantheid groeit nog.',
    'pintro.lvl3': 'Betrouwbaar op wedstrijdtempo - hierop kan men op je rekenen.',
    'pintro.lvl4': 'Creeert er voordeel mee, zelfs onder druk.',
    'pintro.lvl5': 'Beslist momenten ermee; zet de standaard voor anderen.',

    // Player self-testing
    'pself.cardTitle':     'Test jezelf',
    'pself.cardSub':       'Registreer je eigen tijd op een test',
    'pself.title':         'Test jezelf',
    'pself.none':          'Momenteel geen open testsessies. Je coach opent er een wanneer zelftesten aanstaat.',
    'pself.pickSession':   'Kies een testsessie:',
    'pself.pickTest':      'Kies een test om te registreren:',
    'pself.noTests':       'Nog geen tests in deze sessie.',
    'pself.otherSession':  'Kies een andere sessie',
    'pself.save':          'Mijn tijd opslaan',
    'pself.saved':         'Tijd opgeslagen',
    'pself.recordedToday': 'Zonet opgeslagen',
    'pself.selfNote':      'Dit wordt bewaard als een zelf-geregistreerde tijd, apart van de officiele testresultaten.',

    // Reflections (coach side)
    'reflect.title':          'Wedstrijdreflecties',
    'reflect.forTeam':        'Team {team}',
    'reflect.newTitle':       'Vraag je team om te reflecteren',
    'reflect.usesSet':        'Gebruikt de vragen "{set}" voor deze leeftijdsgroep.',
    'reflect.fromLineup':     'Van een recente opstelling (optioneel)',
    'reflect.matchDate':      'Wedstrijddatum',
    'reflect.opponent':       'Tegenstander (optioneel)',
    'reflect.opponentHint':   'bv. Antwerp',
    'reflect.message':        'Bericht aan het team (optioneel)',
    'reflect.messageHint':    'Een korte notitie die boven de vragen verschijnt',
    'reflect.open':           'Reflectie openen',
    'reflect.opened':         'Reflectie geopend voor je team',
    'reflect.needDate':       'Kies de wedstrijddatum.',
    'reflect.reflectedCount': '{n} al gereflecteerd',
    'reflect.closed':         'gesloten',
    'reflect.pastTitle':      'Geopende reflecties',
    'reflect.none':           'Nog geen reflecties geopend. Open er hierboven een na een wedstrijd.',
    'reflect.reviewTitle':    'Reflecties - {date}',
    'reflect.aPlayer':        'Een speler',
    'reflect.blank':          'Geen antwoorden ingevuld.',
    'reflect.commented':      'Beantwoord',
    'reflect.seen':           'Gezien',
    'reflect.newBadge':       'NIEUW',
    'reflect.yourFeedback':   'Jouw feedback (optioneel)',
    'reflect.feedbackHint':   'Schrijf een korte reactie, of markeer gewoon als gezien',
    'reflect.saveFeedback':   'Feedback versturen',
    'reflect.markSeen':       'Enkel als gezien markeren',
    'reflect.feedbackSaved':  'Feedback verzonden',
    'reflect.seenToast':      'Gemarkeerd als gezien',
    'reflect.noSubs':         'Nog niemand heeft gereflecteerd.',
    'reflect.waiting':        'Moet nog reflecteren',
    'reflect.close':          'Reflectie sluiten',
    'reflect.reopen':         'Heropenen',
    'reflect.delete':         'Verwijderen',
    'reflect.deleteTitle':    'Deze reflectie verwijderen?',
    'reflect.deleteBody':     'Dit verwijdert de aanvraag en alle antwoorden van spelers voor deze wedstrijd. Dit kan niet ongedaan gemaakt worden.',

    // Reflections (player side)
    'reflect.p.title':        'Blik terug op je wedstrijd',
    'reflect.p.todoChip':     'NIEUW',
    'reflect.p.feedbackChip': 'NIEUW',
    'reflect.p.sentChip':     'Verzonden',
    'reflect.p.todoSub':      'Je coach vroeg je om te reflecteren - tik om te starten',
    'reflect.p.feedbackSub':  'Je coach heeft gereageerd - tik om te lezen',
    'reflect.p.seenSub':      'Je hebt de reactie van je coach gezien',
    'reflect.p.sentSub':      'Verzonden naar je coach',
    'reflect.p.optional':     '(optioneel)',
    'reflect.p.typeHere':     'Typ je antwoord...',
    'reflect.p.submit':       'Mijn reflectie versturen',
    'reflect.p.incomplete':   'Beantwoord de vragen hierboven voor je verstuurt.',
    'reflect.p.sent':         'Verzonden naar je coach',
    'reflect.p.mineTitle':    'Mijn reflectie',
    'reflect.p.blank':        'Geen antwoorden ingevuld.',
    'reflect.p.yourAnswers':  'Jouw antwoorden',
    'reflect.p.coachTitle':   'Van je coach',
    'reflect.p.coachSeen':    'Je coach heeft je reflectie gezien.',
    'reflect.p.gotIt':        'Begrepen',
    'reflect.p.allDone':      'Je bent helemaal bij',
    'reflect.p.edit':         'Mijn antwoorden aanpassen',

    // Coach Hub
    'chub.title':           'Coachhub',
    'chub.greeting':        'Hallo {name}',
    // The one button that stayed English on an otherwise Dutch hub.
    'seval.new':            '+ Nieuwe evaluatie',
    'chub.tabEvals':        'Evaluaties',
    'chub.tabFeedback':     'Feedback',
    'chub.tabLibrary':      'Bibliotheek',
    'chub.tabTraining':     'Training',
    'chub.tabAges':         'Mijn leeftijden',
    'chub.agesIntro':       'De leeftijdsgroepen die jij opvolgt. Je kan hier elke evaluatie lezen; enkel de trainer die er een schreef kan ze aanpassen.',
    'chub.agesSearchPh':    'Zoek op speler, ploeg of trainer...',
    'chub.agesEmpty':       'Er is nog niets geschreven in jouw leeftijdsgroepen.',
    'chub.agesHeadline':    '{done} van {total} kinderen geëvalueerd',
    'chub.agesHeadlineSub': '{waiting} wachten nog op hun eerste evaluatie dit seizoen',
    'chub.agesTeamLine':    '{done} van {total} geëvalueerd',
    'chub.agesLast':        'laatst {date}',
    'chub.agesNever':       'nog niets',
    'chub.agesWaiting':     'Wachten nog',
    'chub.agesAllDone':     'Iedereen in deze ploeg is geëvalueerd.',
    'chub.agesRecent':      'Meest recent',
    'chub.agesUnknownPlayer': 'Onbekende speler',
    'chub.agesStrong':      'Sterke punten',
    'chub.agesWorking':     'Werkpunten',
    'chub.agesNotes':       'Notities',
    'chub.reflectionAsk':   'Jouw input gevraagd',

    // Seizoensplan oefeningen
    'prac.thisWeek':        'Deze week in het seizoensplan',
    'prac.weekOne':         '{band} · week {n}',
    'prac.weekMany':        'Deze week voor je {count} leeftijdsgroepen',
    'prac.week':            'Week {n}',
    'prac.now':             'Deze week',
    'prac.reference':       'Naslag',
    'prac.allBlocks':       'Hele seizoen',
    'prac.searchPh':        'Zoek oefeningen...',
    'prac.exportPdf':       'Download het volledige seizoensplan (PDF)',
    'prac.time':            'Tijd',
    'prac.area':            'Zone',
    'prac.players':         'Spelers',
    'prac.kit':             'Materiaal',
    'prac.setup':           'Opstelling',
    'prac.organisation':    'Hoe het verloopt',
    'prac.points':          'Coachpunten',
    'prac.cues':            'Sleutelwoorden',
    'prac.detail':          'Waarom deze oefening',
    'prac.progression':     'Uitbreiding',
    'prac.good':            'Hoe het er goed uitziet',
    'prac.adapt':           'Makkelijker of moeilijker maken',
    'prac.easier':          'Makkelijker',
    'prac.harder':          'Moeilijker',
    'prac.questions':       'Vraag aan de spelers',
    'prac.link':            'Hoort bij',
    'prac.drillsFor':       'Oefeningen hiervoor',
    'prac.drillsTitle':     'Oefeningen voor {skill}',
    'prac.drillsSub':       '{count} uit het seizoensplan voor deze leeftijd, meest gerichte eerst.',
    'prac.drillsNone':      'Nog geen oefening in dit leeftijdsplan traint {skill}.',

    'ex.section':        'Van onze coaches',
    'ex.sectionEmpty':   'Nog niemand heeft een oefening gedeeld. De jouwe kan de eerste zijn.',
    'ex.newBtn':         '+ Oefening schrijven',
    'ex.newTitle':       'Nieuwe oefening',
    'ex.editTitle':      'Oefening aanpassen',
    'ex.editSub':        'Gedeeld met elke coach van de club',
    'ex.byLine':         'door {name}',
    'ex.minutes':        '{n} min',
    'ex.state_draft':    'Klad',
    'ex.state_hidden':   'Offline gehaald',
    'ex.state_deleted':  'Ingetrokken door de auteur',
    'ex.state_published': 'Gedeeld',
    'ex.aim':            'Waarvoor dient het',
    'ex.edit':           'Aanpassen',
    'ex.fTitle':         'Naam van de oefening',
    'ex.fTitlePh':       'bv. Vierdoelenspel',
    'ex.fAges':          'Voor welke leeftijden?',
    'ex.fCategory':      'Pijler',
    'ex.fMinutes':       'Minuten',
    'ex.fPlayers':       'Spelers',
    'ex.fPlayersPh':     'bv. 8 tot 12',
    'ex.fAim':           'Waarvoor dient het?',
    'ex.fAimPh':         'Een of twee regels over wat deze oefening traint.',
    'ex.fOrganisation':  'Hoe het verloopt',
    'ex.fOrganisationPh': 'Twee ploegen van vier in een zone van 30 x 20.\nScoor in een van de twee kleine doelen.',
    'ex.fPoints':        'Coachpunten',
    'ex.fPointsPh':      'Hoofd omhoog voor de aanname\nEerste aanname de ruimte in',
    'ex.fKit':           'Materiaal',
    'ex.fKitPh':         'bv. 8 kegels, 4 ballen, 2 minidoelen',
    'ex.fLineHint':      'Een per regel.',
    'ex.fDiagram':       'Schema (optioneel)',
    'ex.fDiagramHint':   'Teken de opstelling op een veld. Veel goede oefeningen hebben geen tekening nodig.',
    'ex.draw':           'Schema tekenen',
    'ex.redraw':         'Schema aanpassen',
    'ex.removeDiagram':  'Schema verwijderen',
    'ex.todo':           'Voor je dit kan delen:',
    'ex.p_title_required': 'Geef het een naam',
    'ex.p_organisation_required': 'Beschrijf hoe het verloopt',
    'ex.p_points_required': 'Voeg minstens een coachpunt toe',
    'ex.p_age_required': 'Kies minstens een leeftijdsgroep',
    'ex.p_title_too_long': 'De naam is te lang',
    'ex.p_too_many_organisation': 'Te veel regels bij hoe het verloopt',
    'ex.p_too_many_points': 'Te veel coachpunten',
    'ex.p_organisation_line_too_long': 'Een van de regels is te lang',
    'ex.p_aim_too_long': 'De omschrijving is te lang',
    'ex.p_kit_too_long': 'De materiaallijst is te lang',
    'ex.p_minutes_out_of_range': 'Minuten moeten tussen 1 en 120 liggen',
    'ex.saveDraft':      'Opslaan als klad',
    'ex.publish':        'Delen met de club',
    'ex.saveLive':       'Wijzigingen opslaan',
    'ex.publishNote':    'Delen zet het meteen in de bibliotheek voor elke coach. Je kan het daarna nog aanpassen of verwijderen.',
    'ex.notReady':       'Er moet nog wat ingevuld worden.',
    'ex.published':      'Gedeeld met de club',
    'ex.savedDraft':     'Opgeslagen als klad, alleen jij ziet het',
    'ex.delete':         'Deze oefening verwijderen',
    'ex.deleteTitle':    'Deze oefening verwijderen?',
    'ex.deleteBody':     'Ze verdwijnt uit de bibliotheek voor iedereen, ook voor jou. De club bewaart een kopie van alles wat opgeslagen is, zodat er niets verloren gaat.',
    'ex.deleted':        'Oefening verwijderd',

    'dta.exSection':      'Oefeningen van coaches',
    'dta.exSectionHint':  'Coaches delen deze zelf, zonder eerst te vragen. Alles wat ze opslaan wordt hier bewaard, ook kladversies en wat ze later introkken, zodat er niets verloren gaat. Offline halen verbergt het meteen voor elke coach en je kan het terugzetten.',
    'dta.exTakeDown':     'Offline halen',
    'dta.exRestore':      'Terugzetten',
    'dta.exTakeDownTitle': '"{name}" offline halen?',
    'dta.exTakeDownBody': 'Ze verdwijnt meteen uit de bibliotheek van elke coach. {name} behoudt zijn kopie en jij kan ze altijd terugzetten.',
    'dta.exRestoreTitle': 'Dit terugzetten in de bibliotheek?',
    'dta.exRestoreBody':  'Elke coach ziet ze weer.',
    'dta.exTakenDown':    'Offline gehaald',
    'dta.exRestored':     'Terug in de bibliotheek',

    'dta.seasonPlans':      'Seizoensplannen',
    'dta.seasonPlansHint':  'Gepubliceerde plannen verschijnen in het tabblad Training van elke coach van die leeftijd, en de oefening van deze week komt op hun startscherm.',
    'dta.planWeeks':        '{weeks} weken + naslag, {total} oefeningen',
    'dta.planPartly':       '{live} van {total} live',
    'dta.planPublishTitle': 'Het {band} seizoensplan publiceren?',
    'dta.planPublishBody':  'Alle {count} oefeningen worden zichtbaar voor elke coach van die leeftijd.',
    'dta.planUnpublishTitle': 'Het {band} seizoensplan depubliceren?',
    'dta.planUnpublishBody':  'Alle {count} oefeningen verdwijnen uit het tabblad Training van de coaches.',
    'dta.planPublished':    '{band} plan gepubliceerd',
    'dta.planUnpublished':  '{band} plan gedepubliceerd',
    'chub.reflectionHint':  'Vul je zelfreflectie in voor het evaluatiegesprek - zo wordt het een gesprek in twee richtingen.',
    'chub.reflectionOpen':  'Zelfreflectie schrijven',
    'chub.reflectionSaved': 'Zelfreflectie opgeslagen',
    'chub.reflectionTitle': 'Zelfreflectie',
    'chub.reflectionFor':   'Voor de evaluatie van {date} ({evaluator})',
    'chub.save':            'Opslaan',
    'chub.evalsNone':       'Nog geen gepubliceerde evaluaties. Zodra de club je evaluatie deelt, verschijnt ze hier.',
    'chub.evalOf':          'Evaluatie van {date}',
    'chub.ackBtn':          'Markeer als gezien',
    'chub.ackDone':         'Gezien op {date}',
    'chub.feedbackNone':    'Nog geen feedbacknotities.',
    'chub.markSeen':        'Markeer als gezien',
    'chub.seen':            'Gezien',
    'chub.librarySoon':     'Clubmateriaal (opwarmingen, oefeningen, focusdocumenten) verschijnt hier binnenkort.',
    'chub.signOut':         'Afmelden',
    'chub.loadFailed':      'Kon niet laden. Probeer het later opnieuw.',
    'chub.evalPlayers':     'Mijn spelers evalueren',
    'chub.evalPlayersOne':  '{team} · kies een pijler',
    'chub.evalPlayersMany': '{count} teams · kies een pijler',
    'chub.pickTeam':        'Welk team?',
    'chub.baseline':        'Basistesten',
    'chub.baselineOne':     '{name}',
    'chub.baselineMany':    '{count} sessies te testen',
    'chub.teamTest':        'Test mijn team',
    'chub.teamTestSub':     'Doe zelf een fitheids- of vaardigheidstest, zonder voorbereiding',
    'chub.teamTestOpen':    '{count} test(s) open',
    'chub.teamTestNew':     '+ Nieuwe test',
    'chub.baselinePick':    'Welke sessie?',
    'chub.lineups':         'Opstellingen',
    'chub.lineupsSub':      'Wedstrijdopstellingen registreren & zien wie speelt',
    'chub.gamePicks':       'Na de wedstrijd',
    'chub.gamePicksSub':    'Je sterke en zwakke spelers na een match',
    'chub.reflect':         'Wedstrijdreflecties',
    'chub.reflectSub':      'Vraag je team om te reflecteren & lees hun antwoorden',
    'chub.pillarPickTitle': 'Welke pijler vandaag?',
    'chub.pillarPickHint':  'Kies waarop je {team} evalueert. Je beoordeelt elke speler op deze pijler, daarna kan je terugkomen voor een andere.',
    'chub.pillarSuggested': 'Voorgesteld',
    'chub.pillarAll':       'Alle pijlers',
    'chub.pillarAllHint':   'Langer: een volledige evaluatie',
    'chub.pillarNow':       'Evaluatie: {pillar}',
    'chub.windowTitle':     'Deze twee weken',
    'chub.windowOpen':      'Open pijler: {pillar}',
    'chub.windowExcluded':  'Er is geen pijler gepland voor {team} deze twee weken. Geniet van de pauze - de volgende opent automatisch.',
    'chub.windowBefore':    'Het seizoensplan is nog niet gestart. Evaluaties openen op het eerste geplande venster.',
    'chub.windowAfter':     'Het seizoensplan is afgelopen voor dit jaar. Bedankt voor alle evaluaties!',
    'chub.windowClosed':    'Er is momenteel geen evaluatievenster open.',
    'chub.windowBreak':     'Evaluaties liggen stil tijdens het zaalseizoen. Ze starten opnieuw zodra de outdoorkalender hervat, en pikken op bij de volgende pijler.',
  },

};

/** Replace {name}-style placeholders with values from params. */
function _interpolate(s, params) {
  if (!params) return s;
  return s.replace(/\{(\w+)\}/g, (m, key) =>
    Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : m
  );
}

/**
 * Translate a key in the active language (CONFIG.i18n.lang), falling back to
 * English, then to the key itself. Optional params fill {placeholders}.
 */
export function t(key, params) {
  const lang = CONFIG.i18n?.lang || 'en';
  const s = STRINGS[lang]?.[key] ?? STRINGS.en[key] ?? key;
  return _interpolate(s, params);
}

// Exported for tests and for a future language-coverage check (which en keys
// are still missing from nl). Not for direct lookup - always go through t().
export { STRINGS };
